import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { countryConfig } from '../country';
import { countryNameEn } from '../lib/country-name-en';
import { LocaleLink as Link, useLocaleNavigate } from '../i18n/locale-link';
import { Seo } from '../components/seo';
import { InvoiceFlow } from '../components/invoice-flow';
import { SubscriptionManagement } from '../components/subscription-management';
import { PaymentHistory } from '../components/payment-history';
import { ContactSupport } from '../components/contact-support';
import { useBackClosesSheet } from '../lib/use-back-closes-sheet';
import { CardSaveModal } from '../components/card-save-modal';
import { useGuestIdentify } from '../components/guest-checkout-identify';
import { OCTO_CONTINUE_ID, OctoSubscribeModal } from '../components/octo-subscribe-modal';
import { Spinner } from '../components/ui/spinner';
import {
  centreInScroller,
  useVisualViewportSheetHeight,
} from '../lib/use-visual-viewport-sheet-height';
import {
  CheckIcon,
  ShieldCheckIcon,
  BellIcon,
  ReceiptIcon,
  RefreshIcon,
  LockIcon,
} from '../components/icons';
import { useAuth } from '../lib/auth-context';
import { usePageTitle } from '../lib/page-title';
import { track } from '../lib/funnel';
import { isSafeCheckoutToken, stashPendingCheckout } from '../lib/pending-checkout';
import { useLocale, useT } from '../i18n/locale-context';
import { localePath } from '../i18n/locale-path';
import {
  fetchPlans,
  fetchPaymentMethods,
  fetchSubscribeHint,
  prepareCheckoutLink,
  startCheckout,
  type Plan,
} from '../lib/billing-client';
import { trackAddPaymentInfo, trackInitiateCheckout, trackViewContent } from '../lib/tracking';
import { CheckoutTermsLine } from '../components/checkout-terms-line';
import { PaymentMarkLogos } from '../components/payment-mark-logos';
import { phStartRecording } from '../lib/posthog';
import { deviceContext, useCoarsePointer } from '../lib/device-context';
import { usePreparedCheckout } from '../lib/use-prepared-checkout';
import { copyLink } from '../lib/copy-link';
import {
  detectInAppBrowser,
  inAppContext,
  needsExternalHandoff,
  cardOnlyCheckout,
  openExternally,
  watchHandoff,
  type InAppBrowser,
} from '../lib/in-app-browser';
import type { CheckoutFlow, CheckoutStep } from '../lib/checkout-events';
import { sendMetaEvent } from '../lib/meta-client';
import { paymentTypeFor } from '../lib/payment-type';
import { contactsWord, daysWord, formatBudget, formatDeadline, pluralRu } from '../lib/format';
import { fetchLandingStats, landingStatsQueryKey } from '../lib/landing-client';
import { socialProofCount } from '../lib/social-proof';
import { COMPANY, HAS_TELEGRAM } from '../lib/company';
import { DateTime } from 'luxon';
import { nicheHintLine } from '../lib/niche-hint';
import { fetchMarketStats } from '../lib/market-client';
import { ApiError } from '../lib/api';
import { CompanyInnModal } from '../components/company-inn-modal';
import { useCheckoutSheetPresence } from '../lib/checkout-presence';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '../components/ui/dialog';
import { planDisplayName } from '../lib/plan-name';
import { uuid } from '../lib/uuid';

const SITE_ORIGIN = countryConfig.brand.siteUrl;

/**
 * Grace period between firing the InitiateCheckout beacons and navigating away
 * to the payment page. Long enough for `fbq`/`gtag` to get their requests onto
 * the wire, short enough to be invisible against a full page load.
 */
const CHECKOUT_BEACON_GRACE_MS = 50;

/**
 * Kazakhstan pays through Stripe (owner, 2026-10-13): ONE button per plan that
 * leaves for Stripe Checkout — a monthly subscription in tenge. None of the
 * Uzbek checkout is rendered there: no Payme card sheet, no gateway chooser, no
 * single payment, no invoice, no «Год» tab. Read from the country config, so
 * the Uzbek build evaluates every branch below exactly as before.
 */
const STRIPE_CHECKOUT = countryConfig.payments.checkout === 'stripe';

/**
 * The marks next to the pay button on a Stripe deployment — exactly the ones
 * the country config lists (Kazakhstan: Visa, Mastercard, Stripe), as their
 * official logos (owner, 2026-10-15; see `payment-mark-logos.tsx` for the
 * sources). Nothing outside that list can appear here.
 */
function PaymentMarks() {
  return <PaymentMarkLogos marks={countryConfig.payments.marks} />;
}

/**
 * How long a guest's address must stand still before the sheet identifies them
 * in the background and prepares their order.
 *
 * 600 ms after the last keystroke, on top of the hook's own client-side syntax
 * check. `/auth/start` mails a code when the address already has an account, so
 * this is the difference between "one request when they stop typing" and "one
 * per character, several of them to strangers' inboxes".
 */
const GUEST_PREPARE_DEBOUNCE_MS = 600;

/** The two billing periods the pricing grid can show. Maps to `billingPeriod`. */
type Period = 'monthly' | 'annual';

/** «Оплачивая, вы соглашаетесь с офертой и политикой конфиденциальности.» */
function ConsentLine() {
  const t = useT();
  return (
    <p className="text-center text-[11.5px] leading-[1.5] text-ink-muted">
      {t('plans.consentPrefix')}{' '}
      <Link to="/offer" className="font-medium text-brand hover:underline">
        {t('plans.consentOffer')}
      </Link>{' '}
      {t('plans.consentAnd')}{' '}
      <Link to="/privacy" className="font-medium text-brand hover:underline">
        {t('plans.consentPrivacy')}
      </Link>
      {/* Translated, not a hardcoded «.». Russian carries the verb in the
          PREFIX («соглашаетесь с …»), Uzbek is verb-final and carries it here —
          with a bare period the Uzbek line read «To'lov qilish orqali siz
          [oferta] va [siyosat].», a subject and two objects with no verb, on
          the sentence that records consent to the offer. */}
      {t('plans.consentSuffix')}
    </p>
  );
}

/**
 * Checkout reassurance strip — the four objections a buyer weighs before
 * clicking any pay button: is my card safe, will I be charged by surprise, can
 * I get my money back, is this legitimate. The card-safety point answers the
 * objection a real prospect named verbatim («напрягает, что номер карты
 * запрашивает… меня это и остановило»); the refund block (third, 2026-09-06)
 * restates the 7-day promise the card modal already makes. Static copy, no CTA
 * — it sits under the grid, above the consent line, so it reads as the page's
 * own footing rather than a per-card claim.
 */
function CheckoutTrust({ wide = false }: { wide?: boolean }) {
  const t = useT();
  const items = [
    {
      icon: <ShieldCheckIcon className="h-5 w-5 text-brand" />,
      title: t('plans.trust.secureTitle'),
      text: t('plans.trust.secureText'),
    },
    {
      icon: <BellIcon className="h-5 w-5 text-brand" />,
      title: t('plans.trust.noSurpriseTitle'),
      text: t('plans.trust.noSurpriseText'),
    },
    {
      icon: <RefreshIcon className="h-5 w-5 text-brand" />,
      title: t('plans.trust.refundTitle'),
      text: t('plans.trust.refundText'),
    },
    {
      icon: <ReceiptIcon className="h-5 w-5 text-brand" />,
      title: t('plans.trust.receiptTitle'),
      text: t('plans.trust.receiptText'),
    },
  ];
  // The strip is exactly as wide as the card grid. Under two cards (≈736px)
  // four columns squeezed every title onto two lines, so it is 2×2; under
  // three cards (`wide`, up to 1112px) a single row of four fits from `lg`.
  return (
    <ul className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${wide ? 'lg:grid-cols-4' : ''}`}>
      {items.map((it) => (
        <li
          key={it.title}
          className="flex items-start gap-2.5 rounded-lg border border-edge bg-bone-surface p-3.5 shadow-m-sm"
        >
          <span className="mt-0.5 shrink-0">{it.icon}</span>
          <div>
            <div className="text-[12.5px] font-bold text-ink">{it.title}</div>
            <p className="mt-0.5 text-[11.5px] leading-[1.45] text-ink-soft">{it.text}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}

/*
 * `BankTransfer` lived here: a static requisites card plus a link to the
 * printable `/schet` page, which the buyer filled in by hand. It is replaced by
 * `components/invoice-flow.tsx`, which issues a NUMBERED invoice with its own
 * payment purpose. `/schet` itself stays routed — the URL is deep-linkable and
 * a few buyers have it bookmarked — it is simply no longer linked from here.
 */

/**
 * Loading skeleton for one pricing card — mirrors `PlanCard`'s shell exactly
 * (name, price row, four feature bullets with a leading check slot, and the
 * full-width CTA), so the plans grid doesn't shift when data resolves.
 */
function PlanCardSkeleton() {
  return (
    <div
      className="flex h-full flex-col rounded-xl border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6"
      aria-hidden="true"
    >
      <span className="mb-2 block h-[15px] w-24 animate-pulse rounded bg-edge" />
      <div className="mb-4 flex items-baseline gap-1.5">
        <span className="block h-[26px] w-28 animate-pulse rounded bg-edge" />
        <span className="block h-[13px] w-16 animate-pulse rounded bg-edge" />
      </div>
      <ul className="mb-6 flex flex-col gap-2.5">
        {Array.from({ length: 4 }).map((_, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="mt-0.5 block h-4 w-4 shrink-0 animate-pulse rounded bg-edge" />
            <span className="block h-[14px] w-[80%] animate-pulse rounded bg-edge" />
          </li>
        ))}
      </ul>
      <span className="block h-[42px] w-full animate-pulse rounded-md bg-edge" />
    </div>
  );
}

/**
 * What a year of Pro buys versus paying month-to-month for the same year, all
 * derived from the live plan rows so the pitch can never outlive the prices.
 */
interface YearlyDeal {
  /** 12 × the monthly price — the struck-through "what it would cost" anchor. */
  refYearUzs: number;
  /** So'm kept by paying yearly instead. */
  savingsUzs: number;
  /** refYearUzs ÷ the annual price. ~2 today: a year costs about six months. */
  multiple: number;
  /** The same fact as a discount (~50), used when `multiple` isn't a clean ×2. */
  percent: number;
}

/** Below this we can't honestly say "×2" — fall back to the generic % savings. */
const X2_MIN_MULTIPLE = 1.9;

/** The rounded "×N" claim when the prices back a real doubling, else null. */
function dealMultiple(deal?: YearlyDeal): number | null {
  return deal && deal.multiple >= X2_MIN_MULTIPLE ? Math.round(deal.multiple) : null;
}

/**
 * Month / Year segmented control (m16: teal-light track, white active pill).
 * The yearly option carries a compact savings hint derived from the live deal.
 */
function PeriodToggle({
  period,
  onChange,
  hint,
}: {
  period: Period;
  onChange: (p: Period) => void;
  hint: string | null;
}) {
  const t = useT();
  const options: { key: Period; label: string }[] = [
    { key: 'monthly', label: t('plans.toggleMonthly') },
    { key: 'annual', label: t('plans.toggleYearly') },
  ];
  return (
    <div
      role="tablist"
      aria-label={t('plans.periodToggleLabel')}
      className="mx-auto mb-7 inline-flex w-full max-w-[380px] items-center gap-1 rounded-pill border border-brand/20 bg-brand-light p-1.5 shadow-m-sm"
    >
      {options.map((o) => {
        const active = period === o.key;
        return (
          <button
            key={o.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.key)}
            className={`inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded-pill text-[15px] font-bold transition-colors ${
              active ? 'bg-bone-surface text-brand shadow-m-sm' : 'text-brand/70 hover:text-brand'
            }`}
          >
            {o.label}
            {o.key === 'annual' && hint && (
              <span className="rounded-pill bg-brand px-2 py-0.5 text-[11px] font-bold uppercase leading-none tracking-[0.02em] text-white">
                {hint}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Small inline glyphs for the checkout block (no icon dep needed). */
function ChevronRightGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className="shrink-0 text-ink-muted"
    >
      <path
        d="M6 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function ChevronDownGlyph({ className = '' }: { className?: string }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={`shrink-0 transition-transform ${className}`}
    >
      <path
        d="M4 6l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
/** A bank card — the «Оплатить разово» glyph (a single hosted charge, no card on file). */
function CardGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden className="shrink-0">
      <rect
        x="1.75"
        y="3.5"
        width="12.5"
        height="9"
        rx="1.8"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path d="M1.75 6.75h12.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M4.25 10.25h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
function BankGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden className="shrink-0">
      <path d="M8 2l6 3H2l6-3z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path
        d="M3.5 6.5v5M6.5 6.5v5M9.5 6.5v5M12.5 6.5v5M2 13.5h12"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Put focus on a control inside whichever checkout sheet is on screen, and
 * bring it into that sheet's own scroller.
 *
 * ⚠️ `preventScroll` + `centreInScroller`, the same rule every focus move in
 * these sheets follows: letting the browser scroll a focused element also lets
 * iOS pan the layout viewport under a `position:fixed` overlay. By id rather
 * than by ref because the two bodies are separate components and only one of
 * them is mounted at a time — a ref into the other is always null.
 *
 * A missing element is a no-op: the sheet is mid-swap, or the body that owns
 * the control is not the one showing.
 */
function focusSheetControl(id: string): void {
  if (typeof document === 'undefined') return;
  const el = document.getElementById(id);
  if (!el) return;
  el.focus({ preventScroll: true });
  centreInScroller(el);
}

/**
 * A single plan/pricing card with grouped feature bullets and its checkout
 * actions, all visible (no disclosure): monthly carries TWO filled CTAs — a
 * card-on-file subscribe (opens the card modal) and «Оплатить разово» (opens a
 * Payme/Click chooser sheet for a single hosted charge; see the history on the
 * checkout branch below); yearly is ONE-TIME ONLY — «оплата по счёту» +
 * one-time Payme/Click, no card-on-file subscribe (a year rarely wants
 * auto-renew, and the server refuses `subscribe-card` for annual plans, so the
 * CTA would only lead to a rejection). The `!isYearly` gate on the subscribe
 * blocks below is what enforces that on the client.
 *
 * - `recommended`: draw the featured accent (brand border + «Популярный» badge).
 * - `deal`: when set (annual Pro only), pitch the year against 12× monthly —
 *   a "×2" badge, the struck-through anchor price, and the so'm saved.
 */
function PlanCard({
  plan,
  recommended = false,
  deal,
}: {
  plan: Plan;
  recommended?: boolean;
  deal?: YearlyDeal;
}) {
  const t = useT();
  const navigate = useLocaleNavigate();
  const [searchParams] = useSearchParams();
  const { status, user, refresh } = useAuth();
  const { locale } = useLocale();
  const isAuthed = status === 'authed';
  /*
   * ⚠️ 'loading' is NOT 'guest', and "methods not fetched yet" is NOT "recurring
   * is off". Both were being read as settled negatives, and the CTA rendered on
   * top of them — so a fast click on a freshly-loaded /plans did the wrong
   * thing, twice over:
   *
   *   auth still resolving   → the GUEST branch rendered «Оформить», whose
   *                            handler navigates to /login. A signed-in user who
   *                            clicked it was bounced to the login screen, which
   *                            reads exactly like being logged out.
   *   methods still fetching → `recurringEnabled ?? false` rendered the ONE-TIME
   *                            Payme CTA. A user who meant to subscribe was sent
   *                            to a single charge — and since one-time now
   *                            carries the 10% surcharge, that click also costs
   *                            them more.
   *
   * Both windows are short, which is why this survived: it only fires if you
   * click before the page settles, which is exactly what someone who has landed
   * here on purpose does.
   */
  const authKnown = status !== 'loading';
  const [showBank, setShowBank] = useState(false);
  /*
   * THE GUEST BUYS ON THE SHEET HE IS ALREADY LOOKING AT (2026-09-19).
   *
   * Measured 2026-09-18 over 30 days: 8,531 guests saw a paywall, 1,757 clicked
   * its CTA, 2,413 reached /plans, 245 picked a plan — and 0 reached checkout,
   * because the pick used to end in `navigate('/login')`. The identification is
   * mandatory (`payment_orders.user_id` is NOT NULL and stays so), but sending
   * the buyer to another page for it is not: 98 of a 177-guest wall cohort
   * (55%) never came back.
   *
   * Two shapes have now been rejected. MR !565 asked «кто вы» on its own sheet
   * and then opened the card sheet — «по сути обычная рега через модалку». The
   * next attempt put the field on the card sheet but gave the guest a DIFFERENT
   * card sheet: no Payme/Octobank strip («нельзя выбрать метод оплаты, это
   * просто эмуляция оплаты») and a Telegram button behind an «или» divider
   * between the address and the card, which read as registration again.
   *
   * What is built here is the third: the guest opens THE SAME sheet a member
   * opens — subscription, разовая, счёт — with exactly one extra field, email,
   * above the payment method. Nothing else added, nothing removed.
   *
   * ⚠️ Its own flag, and NOT `subscribeOpen`, because the sheet must survive the
   * session appearing underneath it: `/auth/start` flips `isAuthed` halfway
   * through the buyer's typing, and anything mounted inside an `isAuthed` branch
   * would unmount at that exact moment and take the typed card with it. This
   * flag is set once, by the guest's own click, and nothing about auth clears it.
   */
  const [guestSheetOpen, setGuestSheetOpen] = useState(false);
  /*
   * ⚠️ MEMOIZED, and it MUST stay memoized. `card-save-modal` builds its
   * `dismiss` with `useCallback(…, [onClose, …])` and its focus trap runs on
   * `[open, dismiss, step]`. The guest's email lives HERE, in the parent, so
   * every keystroke re-renders this card — and with an inline arrow for
   * `onClose` that gave the modal a new `dismiss` each time, the trap re-ran,
   * its cleanup restored focus to the «Оформить» button, and the buyer could
   * type exactly ONE character of their address before the caret was taken away.
   * Caught 2026-09-19 by `plans.guest-checkout.test.tsx` («EMAIL= n»).
   */
  const closeGuestSheet = useCallback(() => setGuestSheetOpen(false), []);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [octoOpen, setOctoOpen] = useState(false);
  // Same rule for the member's sheet: an inline arrow here rebuilt the modal's
  // `dismiss` on every re-render of this card and, until the modal keyed its
  // focus restore on `open` alone, cost the buyer the caret mid-number.
  const closeSubscribeSheet = useCallback(() => {
    setSubscribeOpen(false);
    setOctoOpen(false);
  }, []);
  // OCTO-allowlisted users can switch gateway inside the modal (Payme inline vs
  // Octobank redirect). Defaults to PAYME (proven, no-CVV); Octobank is the
  // "also Visa/Mastercard" alternative. The radio cards swap which modal renders.
  const [payMethod, setPayMethod] = useState<'octo' | 'payme'>('payme');
  /*
   * IS THE SHEET ON SCREEN CHANGING GATEWAY, rather than opening?
   *
   * Picking a gateway swaps which of two components renders, so the outgoing
   * one unmounts and the incoming one mounts — and `CardSaveModal` is the only
   * one of the pair with an entrance animation. Octobank → Payme therefore
   * replayed a full bottom-sheet slide-up behind a re-created backdrop, while
   * Payme → Octobank swapped in one frame (Igor, 2026-09-20: one direction is
   * smooth, the other «как будто другую модалку вызывает»). This flag is what
   * tells the card sheet not to make an entrance out of a radio press.
   *
   * ⚠️ It says NOTHING to the funnel. A swap is not a dismissal and not a new
   * checkout: it calls neither `onClose` (which is what writes
   * `checkout_dismissed`) nor `fireBeginCheckout` (`checkout_opened`), and
   * that is unchanged — the sheets keep the one `checkoutId` the CTA minted.
   * Cleared by every path that opens a checkout FRESH, so the first sheet of
   * the next checkout animates in as it always has.
   */
  const [methodSwap, setMethodSwap] = useState(false);
  /** Pick a gateway on an OPEN sheet — i.e. a swap, not an open. */
  const switchMethod = useCallback((m: 'octo' | 'payme') => {
    setMethodSwap(true);
    setPayMethod(m);
  }, []);
  // The «Оплатить разово» gateway chooser (bottom sheet on phones, centred
  // dialog on desktop). Opened by the second CTA on a monthly card; picking a
  // gateway inside it starts the hosted one-time checkout.
  const [onceOpen, setOnceOpen] = useState(false);
  // The one-time sheet's own element, for the keyboard hook (see its className).
  const onceSheetRef = useRef<HTMLDivElement>(null);
  useVisualViewportSheetHeight(onceOpen, onceSheetRef);
  /*
   * Was this sheet opened by someone with no account? Pinned when it OPENS, not
   * re-read on the gateway tap: that tap identifies first, so by the time the
   * event is written the live `isAuthed` already says "signed in" and every
   * guest one-time checkout would file itself under the authed path.
   */
  const [onceAsGuest, setOnceAsGuest] = useState(false);
  /*
   * Which gateway the buyer has PICKED in the one-time sheet — a selection now,
   * not a payment. The tap used to be both at once, which left no moment at
   * which the order could exist before the pay gesture. See
   * `usePreparedCheckout` for what that cost (12 orders, 0 completions in the
   * 24 h to 22.09).
   *
   * `null` until they choose, EXCEPT when only one gateway is on offer: there is
   * no choice to make then, so it is made for them and the order starts
   * preparing as the sheet opens.
   */
  const [onceGateway, setOnceGateway] = useState<'payme' | 'click' | null>(null);
  /*
   * A touch screen — decides the one-time gateway ORDER (Click first on a touch
   * screen — see `payOnceGateways` below). Reactive rather than a one-shot read
   * so the DevTools device toolbar — the only way anyone checks this locally —
   * flips it without a reload.
   */
  const coarsePointer = useCoarsePointer();
  /*
   * The two sheets this card owns directly — the счёт dialog and the one-time
   * gateway chooser — announce themselves for the same reason the card and
   * Octobank sheets do: both can turn a guest into an account mid-flow
   * (`bankIdentify` / `onceIdentify` → `/auth/start`), and `OnboardingModal`
   * must not open over the sheet that just did it. See `checkout-presence.ts`.
   */
  useCheckoutSheetPresence(showBank);
  useCheckoutSheetPresence(onceOpen);
  /*
   * THE HAND-OFF THAT DID NOT HAPPEN.
   *
   * `openExternally` fires an `intent://` URL and returns; if the WebView
   * swallows the scheme, nothing happens and the buyer waits for a page that
   * never comes. Non-null here means the 2.5 s watcher expired with this page
   * still on screen — so we show the link and let them carry it out by hand.
   * See `watchHandoff` for what counts as "it took" and why the detector errs
   * towards silence.
   */
  const [handoffStuck, setHandoffStuck] = useState<string | null>(null);
  const [handoffCopied, setHandoffCopied] = useState(false);
  /** Cancels the armed watcher. One at a time; a second attempt replaces it. */
  const handoffWatch = useRef<(() => void) | null>(null);
  const cancelHandoffWatch = useCallback(() => {
    handoffWatch.current?.();
    handoffWatch.current = null;
  }, []);
  // Nothing may outlive the card: an unmounted sheet firing `checkout_failed`
  // would file a hand-off failure against a checkout nobody is looking at.
  useEffect(() => cancelHandoffWatch, [cancelHandoffWatch]);
  // The metaEventId minted when the sheet opened (Pixel/CAPI InitiateCheckout
  // fired then); the gateway pick reuses it so the legs of one attempt join.
  const [onceMetaEventId, setOnceMetaEventId] = useState<string>();
  /*
   * Which in-app browser we are inside — read AFTER mount, never during render.
   *
   * This card is prerendered, and the prerender has no `navigator`. Deriving it
   * inline would make the server HTML and the first client render disagree the
   * moment a real Instagram user opens the page, which is a hydration mismatch
   * on the page people pay from. The hint below simply appears a tick later.
   */
  const [inApp, setInApp] = useState<InAppBrowser>({ app: null, os: 'other' });
  useEffect(() => setInApp(detectInAppBrowser()), []);
  // metaEventId minted when the «Подписаться» button is clicked, threaded into the
  // subscribe request so the server CAPI InitiateCheckout dedups against the
  // browser Pixel event we fire on that same click.
  const [subscribeMetaEventId, setSubscribeMetaEventId] = useState<string>();
  /*
   * Which plan the OPEN modal is charging. Starts as this card's plan and
   * changes if the buyer takes the year upsell inside the sheet, so they never
   * have to close it and find the annual card in the grid behind.
   */
  const [subscribePlan, setSubscribePlan] = useState<Plan>(plan);

  /*
   * Which gateways this visitor may use, and whether card-on-file auto-renewal
   * is open to them. One request, shared across cards.
   *
   * ⚠️ Runs for GUESTS too since 2026-09-19 (`/billing/methods` is `@Public()` +
   * `OptionalJwtAuthGuard`). The guest buys on the same sheet a member does, and
   * that sheet's Payme/Octobank strip comes from here — without it the guest got
   * a bare card form with no named processor, which is exactly the «просто
   * эмуляция оплаты» that got the last attempt reverted. The response is a pure
   * function of the caller's own email against server-side allowlists and
   * carries nothing personal; for an anonymous caller it reports whatever is
   * open to everyone.
   */
  const { data: methodsData, isPending: methodsPending } = useQuery({
    queryKey: ['billing', 'methods'],
    queryFn: fetchPaymentMethods,
    enabled: authKnown,
  });
  const recurringEnabled = methodsData?.recurringEnabled ?? false;
  /*
   * CARD ONLY inside the Instagram / Telegram WebView on ANDROID (owner,
   * 2026-09-30: «проще подписку дать как единственную опцию, просто у них в
   * браузере по-другому назвать чтобы не смущало»).
   *
   * Measured on prod for that browser: the one-time sheet 19–27.09 — 37 people
   * opened it, 29 pressed pay, 14 reached the Payme/Click page, 1 paid; the
   * phone-number SMS invoice that replaced it on 27.09 — 0 of 11 paid; our own
   * card sheet — 4 of the 5 purchases from this browser since 19.09. So the
   * monthly card there offers the card sheet alone, labelled «Оплатить картой»
   * with the renewal said in words under the button, and the SMS sheet is gone.
   *
   * Only the FACE changes: the button opens the same card sheet «Подписаться»
   * does, through the same `openSubscribeModal` and the same events — Meta's
   * InitiateCheckout included, exactly as before. Applies to the monthly
   * subscribe branches only: a Payme recurring outage (no subscription to sell)
   * still degrades to the one-time buttons here like everywhere else, and the
   * yearly card is untouched.
   */
  const cardOnly = cardOnlyCheckout(inApp);

  // The subscribe-modal personalisation: a live count for the value pill and the
  // channel the renewal reminder will use. Authed-only, one request shared across
  // cards (same queryKey). Graceful fallback while pending/failed: no pill, and
  // the timeline promises email (never a Telegram it can't deliver).
  const { data: hintData } = useQuery({
    queryKey: ['billing', 'subscribe-hint'],
    queryFn: fetchSubscribeHint,
    enabled: isAuthed,
    staleTime: 5 * 60 * 1000,
  });
  const nicheHint = hintData?.nicheHint ?? undefined;
  // Named in the terms line («напомним за 7 дней по Telegram / по почте»):
  // the channel the T-7 reminder will actually use, from the same hint call.
  const reminderChannel = hintData?.reminderChannel ?? 'email';
  // The live-count pill on the card itself (2026-09-06). A signed-in buyer sees
  // the count for THEIR saved searches (the same `nicheHint` the modal shows,
  // so the two can never disagree); a guest sees the whole catalogue's open
  // count from the public, Redis-cached market stats — same queryKey as
  // `use-deadline-pitch`, so a visit that already fetched it pays nothing.
  const { data: market } = useQuery({
    queryKey: ['market', 'stats', locale],
    queryFn: () => fetchMarketStats(locale),
    enabled: authKnown && !isAuthed,
    staleTime: 5 * 60_000,
  });
  const livePill: string | null = isAuthed
    ? nicheHint
      ? nicheHintLine(nicheHint, t)
      : null
    : market && market.activeTenders > 0
      ? t('plans.openNow', {
          count: formatBudget(String(market.activeTenders)) ?? String(market.activeTenders),
          word: pluralRu(
            market.activeTenders,
            'открытый тендер',
            'открытых тендера',
            'открытых тендеров',
          ),
        })
      : null;
  // OCTO gateway is offered only to allowlisted users (server returns 'octo' in
  // `methods` only for them). When present, the single subscribe CTA routes to
  // the Octobank redirect modal instead of the Payme inline card modal.
  const octoGateway = methodsData?.methods?.includes('octo') ?? false;
  // Click gateway (my.click.uz hosted redirect). When PAYMENT_METHODS lists
  // `click`, the server returns it in `methods` and we offer a one-time Click
  // payment alongside the Payme one-time button. One-time only — no card-on-file
  // / auto-renewal for Click.
  const clickGateway = methodsData?.methods?.includes('click') ?? false;

  /*
   * ── THE GUEST'S ONE EXTRA FIELD ───────────────────────────────────────────
   *
   * Three instances, one per sheet, because a half-typed address carried
   * between two sheets a buyer opened in turn would be a state bug, not a
   * convenience. Each is inert for a signed-in buyer: `node` is null, `active`
   * is false and `ensure()` resolves true without a request, so every authed
   * path below is byte-for-byte the one it has always been.
   *
   * `enabled` is read once and LATCHED inside the hook — `/auth/start` flips
   * `isAuthed` mid-sheet, and a live read would pull the field out from under
   * the buyer at the exact moment it succeeded.
   */
  const guestNow = authKnown && !isAuthed;
  /* The card sheet's own field skin, so the extra input is indistinguishable
     from the card number beside it. Mirrors `inputBase + inputOk` / `labelClass`
     in `card-save-modal.tsx`; if those change, change these. The «Оплатить
     разово» sheet has no fields of its own and borrows the same pair, so the two
     sheets a buyer compares side by side ask for the address identically. */
  /* ⚠️ 16px, and not a pixel less, at phone widths. iOS Safari ZOOMS the page
     when an input whose computed font-size is under 16px takes focus, and it
     never zooms back out — so one tap on a 15px field leaves the whole checkout
     a few percent too wide and clipped on the right for the rest of the session
     (Igor, 2026-09-21: «после ввода зум становится на 1–5%»). `md:` variants
     are free to be smaller; a desktop browser does no such thing. NEVER fix
     this with `maximum-scale` on the viewport meta — that takes pinch-zoom away
     from everyone to spare us a class. */
  const SHEET_INPUT =
    'h-11 w-full rounded-lg border border-edge bg-bone-surface px-3 text-[16px] text-ink placeholder:text-ink-muted focus:border-brand focus:outline-none';
  const SHEET_LABEL = 'mb-1.5 block text-[12.5px] font-semibold text-ink';
  /** …and its marked variant — `inputBase + inputErr` in `card-save-modal.tsx`. */
  const SHEET_INPUT_ERR =
    'h-11 w-full rounded-lg border border-status-closing-text bg-bone-surface px-3 text-[16px] text-ink placeholder:text-ink-muted focus:border-status-closing-text focus:outline-none';
  /** The subscription sheet. Also the Octobank sheet: picking Octobank
   *  identifies first and then swaps the body, so the address is asked once. */
  const cardIdentify = useGuestIdentify({
    enabled: guestNow,
    /*
     * ENTER in the address moves to the next control of whichever body is on
     * screen — the card number on Payme, «Продолжить на Octobank» on Octobank.
     * ONE hook instance feeds both bodies, so only /plans knows which; hence
     * the callback rather than a rule inside the hook.
     *
     * It moves focus and presses nothing. On the Octobank body the next control
     * is the button that LEAVES THE SITE, and an Enter that redirects to a
     * payment page is the most surprising thing this key could do; a focused
     * button is one deliberate press away and says where it goes.
     */
    onEnter: () => focusSheetControl(payMethod === 'octo' ? OCTO_CONTINUE_ID : 'card-number'),
    inputClassName: SHEET_INPUT,
    inputErrorClassName: SHEET_INPUT_ERR,
    labelClassName: SHEET_LABEL,
  });
  /*
   * …and the same field on the «Оплатить разово» gateway sheet.
   *
   * One-time is the path a card-shy buyer takes («напрягает, что номер карты
   * запрашивает»), and it was closed to guests entirely — the whole one-time
   * block renders only for a signed-in user. Now a guest sees it too: the
   * address goes above the gateway buttons, those buttons stay disabled until it
   * is well-formed, and the tap identifies before it redirects.
   */
  const onceIdentify = useGuestIdentify({
    enabled: guestNow,
    inputClassName: SHEET_INPUT,
    inputErrorClassName: SHEET_INPUT_ERR,
    labelClassName: SHEET_LABEL,
  });
  /** …and on the «оплата по счёту» sheet — the yearly guest's whole path. Its
   *  field skin is `InvoiceFlow`'s, not the card sheet's, for the same reason. */
  const bankIdentify = useGuestIdentify({
    enabled: guestNow,
    /* 16px under `md`, 15px above it — see the note on `SHEET_INPUT`. The
       счёт's own fields carry the same pair, so the three inputs on this sheet
       still agree at every width. */
    inputClassName:
      'h-[46px] w-full rounded-md border-[1.5px] border-edge bg-bone-surface px-3 text-[16px] text-ink outline-none focus:border-brand md:text-[15px]',
    inputErrorClassName:
      'h-[46px] w-full rounded-md border-[1.5px] border-status-closing-text bg-bone-surface px-3 text-[16px] text-ink outline-none focus:border-status-closing-text md:text-[15px]',
    labelClassName: 'mb-1.5 block text-[12.5px] font-semibold text-ink-soft',
  });

  // The in-modal gateway chooser (only for OCTO-allowlisted users): two radio
  // cards, Payme (inline, no CVV) and Octobank (redirect, also Visa/Mastercard).
  // Rendered under the header of BOTH subscribe modals; picking one swaps which
  // modal shows, so the cards stay put and only the payment body below changes.
  // The gateway BRAND is its own wordmark (Payme / Octobank), shown in place of a
  // plain text title. The accepted card schemes sit on the right; each carries its
  // own height because the Uzbek marks (Uzcard/Humo) read small next to the wide
  // Visa/Mastercard wordmarks — so the local ones are set a touch larger and the
  // international ones a touch smaller. `iconAlt` carries the brand name for the
  // button's accessible name (scheme logos are decorative, alt="").
  //
  // ⚠️ All four marks are ~20% bigger since 2026-09-20 (Igor: they read as
  // fine print). 22→26 for the local pair, 12→14 Visa, 18→22 Mastercard, which
  // keeps their relative weights. Measured at 375px afterwards: radio 18 + gap
  // 10 + wordmark 104 + gap 10 + marks 131 + 3 gaps 24 = 297 inside a 311px
  // content box, so the Octobank row still holds one line — and it cannot be
  // pushed over by a system font size, because after the captions came off
  // there is no TEXT left in the button at all. 320px still wraps, by design.
  //
  // ⚠️ 2026-10-11: that arithmetic held at 375px and NOT at 360px, which is the
  // width of most Android phones here. Measured on a real 360px phone: content
  // box 292px, row 132 + 10 + 154.7 = 296.7 — over by under 5px, so the Octobank
  // marks dropped to a second line under a one-line Payme card (owner: «должно
  // быть в одну строчку, как у Payme»). The fix is the WORDMARK box, 104 → 88px
  // (see below), not the marks: they stay at the sizes the owner asked for on
  // 2026-09-20, and the row is 280.7px with 11px to spare at 360px. It also
  // brings the two wordmarks closer in size — Octobank 88×17 beside Payme 70×20
  // instead of 103×20. 320px still wraps, by design.
  //
  // ⚠️ No text caption under the wordmark any more (Igor, 2026-09-20). It spelled
  // out «Uzcard · Humo» / «Uzcard · Humo · Visa · Mastercard» beside the very
  // logos that say it — the same list twice on one row, and the longer of the two
  // was the string that forced the whole strip to wrap at 320px.
  const methodCards = [
    {
      m: 'payme' as const,
      icon: '/img/payme.png',
      iconAlt: 'Payme',
      logos: [
        { src: '/img/uzcard.svg', h: 'h-[26px]' },
        { src: '/img/humo.svg', h: 'h-[26px]' },
      ],
    },
    {
      m: 'octo' as const,
      icon: '/img/octobank.svg',
      iconAlt: 'Octobank',
      // Octobank takes all four schemes, so it shows all four — the local marks
      // at the same height as on the Payme card, the wide international
      // wordmarks a touch shorter so the row reads as one line (2026-09-06).
      logos: [
        { src: '/img/uzcard.svg', h: 'h-[26px]' },
        { src: '/img/humo.svg', h: 'h-[26px]' },
        { src: '/img/visa.svg', h: 'h-[14px]' },
        { src: '/img/mastercard.svg', h: 'h-[22px]' },
      ],
    },
  ];
  /*
   * ⚠️ The gateway switch is FREE — for a guest exactly as for a member.
   *
   * It was briefly gated on identification, so that Octobank could not be
   * reached without an account behind it. Igor killed that the same hour: «по
   * Octobank почему там нет поля? а если я на первой модалке не ввёл email и
   * перешёл в Octobank — что?» Gating made the tap do nothing visible — the
   * radio stayed on Payme and the error landed on a field in the body the buyer
   * had just left. Both bodies carry the address instead (see `identify` on
   * `OctoSubscribeModal`), and «Продолжить на Octobank» is what identifies,
   * because that is the control that actually leaves the site.
   */
  const methodTabs = octoGateway ? (
    <div className="mb-4 flex flex-col gap-2">
      {methodCards.map(({ m, icon, iconAlt, logos }) => {
        const active = payMethod === m;
        return (
          <button
            key={m}
            type="button"
            onClick={() => switchMethod(m)}
            aria-pressed={active}
            /*
             * `flex-wrap`, not one rigid row. Measured 2026-09-16: the Octobank
             * scheme strip is a hard 134px (four marks + gaps) and was
             * `shrink-0`, so at a 320px viewport the middle column was squeezed
             * to 80px — narrower than the 103px octobank wordmark, which
             * preflight's `max-width:100%` then shrank to 76px and letterboxed,
             * and narrower than the unbreakable word «Mastercard», which
             * overflowed onto the marks outright at larger system font sizes
             * (+27px at 2×). Letting the strip drop to its own line is the only
             * layout that survives 320px: the two cannot both fit.
             */
            className={`flex w-full flex-wrap items-center gap-x-2.5 gap-y-2 rounded-lg border px-3 py-2.5 text-left transition-colors ${
              active ? 'border-brand bg-brand-dim' : 'border-edge bg-bone-surface hover:bg-bone'
            }`}
          >
            {/* ⚠️ THE DOT AND THE WORDMARK ARE ONE GROUP (Igor, 2026-09-20:
                «кружок не на одной линии с логотипом»). They used to be two
                separate children of a `flex-wrap` row, which centres each item
                inside ITS OWN flex line — so the moment the row wrapped, the
                dot could end up on a line of its own, 18px tall, above a 20px
                wordmark on the next. `shrink-0` keeps the pair off the wrap
                decision entirely: it is the MARKS that drop to a second line
                when 320px cannot hold both columns, which is the wrap this
                layout was designed around. */}
            <span className="flex shrink-0 items-center gap-2.5">
              <span
                className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-[1.5px] ${
                  active ? 'border-brand' : 'border-edge'
                }`}
              >
                {active && <span className="h-2.5 w-2.5 rounded-full bg-brand" />}
              </span>
              {/* Fixed 88×20 box + object-contain (104 → 88 on 2026-10-11, see the
                  note above `methodCards`): the wide octobank wordmark (300×58)
                  is held to 88×17 by the box width, the narrower Payme mark
                  (240×69 → 70×20) left-aligns inside it, and both scheme strips
                  start at the same x — so the two cards agree and both fit one
                  line at 360px. */}
              <span className="block h-5 w-[88px] max-w-full">
                <img
                  src={icon}
                  alt={iconAlt}
                  className="h-full w-full object-contain object-left"
                />
              </span>
            </span>
            {/* `flex-1 basis-auto` with NO shrink: the marks keep their true
                widths and, when they no longer fit, wrap onto a second line
                right-aligned instead of crushing the wordmark beside them. */}
            <span className="flex flex-1 basis-auto flex-wrap items-center justify-end gap-x-2 gap-y-1">
              {logos.map((l) => (
                <img key={l.src} src={l.src} alt="" className={`${l.h} w-auto`} />
              ))}
            </span>
          </button>
        );
      })}
    </div>
  ) : undefined;
  /*
   * The CTA is only truthful once BOTH facts are in. `methodsPending` is
   * qualified by `isAuthed` deliberately: a disabled react-query never leaves
   * 'pending', so an un-gated check would freeze the guest CTA forever.
   *
   * ⚠️ The qualifier STAYS even though the methods query now runs for guests
   * too (2026-09-19). Making the guest CTA wait on it would be the worse trade.
   * The AUTHED CTA has to wait because its two branches spend different amounts
   * of money — a subscription or the +10% one-time — so a click on a
   * not-yet-decided button buys the wrong thing at the wrong price. A guest's
   * CTA opens a SHEET either way, and that sheet fills in its own Payme/Octobank
   * strip the moment the query lands, because `methodTabs` is recomputed on
   * every render. So the guest gets the button immediately and still never sees
   * a half-built sheet.
   */
  const ctaReady = authKnown && (!isAuthed || !methodsPending);

  // Begin Checkout fires on the CLICK of any pay button (card / Payme / invoice),
  // by product decision — we want to see payment intent even when the checkout is
  // later abandoned. The shared metaEventId lets the server-side CAPI
  // InitiateCheckout (fired on the actual request) dedup against this browser
  // Pixel event. The conversion funnel dedups per identity, so counting clicks
  // does not inflate it.
  const fireBeginCheckoutFor = (
    target: Plan,
    flow: CheckoutFlow,
    opts: {
      /** Which app the hosted one-time buyer chose (Payme / Click). */
      provider?: 'payme' | 'click';
      /**
       * Reuse an id minted earlier — the «Оплатить разово» sheet fires the
       * Pixel/CAPI InitiateCheckout when it OPENS (symmetric with the subscribe
       * button, which fires before its modal), and the gateway pick inside it
       * must then join the same attempt without firing Meta a second time.
       */
      metaEventId?: string;
      /**
       * `'guest'` — this checkout was opened by someone with no account, who
       * types their address on the payment sheet itself and gets one created out
       * of the purchase (see `useGuestIdentify`).
       *
       * A META KEY on the existing event, deliberately not a new event name: the
       * ladder below it (`checkout_field_completed` → `payment_info_submitted` →
       * `otp_requested` → `otp_submitted` → `checkout_started` → `paid`) is the
       * same one, and a second name would split every rung's denominator in two.
       * Absent on the authed path, so no existing series moves.
       */
      via?: 'guest';
      /**
       * Keep the two META legs back — everything first-party still fires here,
       * at the same moment and with the same keys.
       *
       * Set on every GUEST path (2026-09-22). Meta's CBO campaign optimises on
       * InitiateCheckout, and since guest checkout shipped on 19.09 a visitor
       * with no account could open a payment sheet and produce one. Measured
       * 19–21.09: guests converted 1 of 31 opens against members' 11 of 86 —
       * so IC had quietly become "opened the sheet" and the optimiser was
       * learning on it. A guest's IC now waits for a QUALIFIED gesture (the
       * first digit of a card number, or the press that leaves for a hosted
       * provider) and is fired by `fireDeferredMetaIc` under this same
       * `metaEventId`.
       *
       * ⚠️ `checkout_opened` is NOT deferred with it. It is the funnel's
       * denominator and the registry documents it firing on the pay control;
       * moving it would silently re-base every rate below it.
       */
      deferMeta?: boolean;
    } = {},
  ): string => {
    // Doubles as the `checkoutId`: the server stamps it on `checkout_started`
    // and every client-side modal step carries it, so the legs of one checkout
    // attempt join by a shared id instead of by identity across two pipelines.
    const metaEventId = opts.metaEventId ?? uuid();
    const provider = opts.provider;
    const pixelAlreadyFired = opts.metaEventId != null;
    /*
     * UZS, not the plan's USD list price.
     *
     * GA4's only monetary field was `plan.priceUsd` — the exact number this
     * branch removed from Meta for being wrong. The USD figures are hand-set
     * per plan with no shared rate (implied 6,583–12,626 UZS/USD, `ip-monthly`
     * about half the others) and, being list price, they hide every discount.
     * Leaving GA4 on USD would also guarantee that a future GA4 `purchase` —
     * which must be UZS to match the charge — disagreed with its own
     * `begin_checkout`.
     */
    /*
     * ⚠️ The amount THIS flow will actually charge, not the plan's headline
     * price — they stopped being the same number when paying once became 10%
     * dearer than subscribing.
     *
     * Sending `priceUzs` for every flow would understate the one-time and
     * invoice journeys by exactly the surcharge: Meta's optimiser would train
     * InitiateCheckout on 399 000 against a Purchase of 439 000, and GA4's
     * Purchase Journey would show a value jump between two adjacent steps of
     * the same journey. That is the failure this file already fixed once, when
     * GA4 was left on a hand-set USD list price while everything else carried
     * UZS — same defect, smaller multiplier.
     *
     * `card` is the subscription and pays `priceUzs`; `hosted` and `invoice`
     * are both single payments and pay the surcharged price (BankTransfer is
     * handed the same figure on screen).
     */
    /*
     * ⚠️ `Number(null)` is 0, not NaN, so an API without `priceUzsOneTime` would
     * quietly report a 0-value InitiateCheckout — Meta's optimiser trained on
     * free intent, with nothing in any log to say why. Fall back to the
     * subscription price: understating by the surcharge is a rounding error next
     * to claiming the checkout was worth nothing.
     */
    const valueUzs =
      flow === 'card' ? Number(target.priceUzs) : Number(target.priceUzsOneTime ?? target.priceUzs);
    // annual / subscription / onetime — derived ONCE and handed to both legs
    // below, so the Pixel event and its CAPI twin cannot disagree.
    const paymentType = paymentTypeFor(target.billingPeriod, flow);
    if (!pixelAlreadyFired && !opts.deferMeta) {
      trackInitiateCheckout(metaEventId, {
        value: valueUzs,
        currency: countryConfig.currency.code,
        planKey: target.key,
        paymentType,
      });
    }
    /*
     * Mirror the click to the server-side CAPI so the buyer's hashed email/name
     * attach to InitiateCheckout. The browser Pixel's Advanced Matching carries
     * only `external_id`, and the server-side IC inside the checkout request
     * fires only on submission — so a click that abandons (the majority) reached
     * Meta with no identity beyond the visitor id, which is why `em`/`fn`/`ln`
     * coverage on IC ran at roughly half that of Subscribe/Registration. Same
     * `metaEventId` → Meta dedups the pair, no double count. Authed only: an
     * anonymous click has nothing to add and the endpoint drops it.
     */
    if (isAuthed && !pixelAlreadyFired && !opts.deferMeta) {
      sendMetaEvent('InitiateCheckout', metaEventId, target.key, undefined, undefined, paymentType);
    }
    /*
     * THE DENOMINATOR. Fired on the click, before anything can fail.
     *
     * The card flow's server-side `checkout_started` is emitted inside
     * POST /billing/subscribe-card, which the modal calls only AFTER the card
     * has tokenized AND the OTP has verified — so it marks the END of checkout,
     * not the start, and everything before it was invisible. That is why the
     * step read as a black box: there was no event at the top of it.
     *
     * `device` rides here because the open question is whether people abandon
     * or the form breaks, and the modal has been suspected on mobile before. A
     * completion rate that splits by viewport answers that; one that does not
     * cannot.
     *
     * INVOICE IS EXCLUDED, for the same reason it is kept out of
     * `checkout_started`: «оплата по счёту» is settled by bank transfer and
     * activated by hand, so it can never produce the `paid` that follows. Since
     * this event is the DENOMINATOR, counting those clicks would depress every
     * rate below it by exactly the people who chose a method that works — the
     * skew the `invoice_requested` comment argues against, reintroduced one
     * layer up. Invoice intent is recorded by `invoice_requested`; the Meta and
     * GA4 legs above still fire, because there it IS ordinary payment intent.
     */
    if (flow !== 'invoice') {
      /*
       * Remember WHAT is being bought, alongside the tender the page was opened
       * with. Written on the same branch as `checkout_opened` and for the same
       * population: an invoice is settled by bank transfer and activated by
       * hand, so there is no return-from-payment moment to spend the context on.
       * `stashPendingCheckout` merges, so this never drops the `?tender` the
       * page stashed on mount.
       *
       * `intent: 'pay'` is what separates this from the mount-time stash: only
       * a PRESSED pay control may send the visitor back to `/plans` with a
       * sheet opening by itself after sign-in (see `postAuthPath`).
       */
      stashPendingCheckout({ planKey: target.key, intent: 'pay' });
      track('checkout_opened', {
        checkoutId: metaEventId,
        plan: target.key,
        flow,
        // Which app the hosted one-time buyer chose (Payme / Click). Absent on
        // the card flow; the funnel cohort tests only `flow`, so an extra key
        // cannot move a buyer between ladders.
        ...(provider ? { provider } : {}),
        // The guest one-sheet path — absent for a signed-in buyer, so every
        // existing breakdown of this event keeps its old shape.
        ...(opts.via ? { via: opts.via } : {}),
        ...deviceContext(),
        // Which in-app browser the checkout was opened in, absent in an
        // ordinary one. `device`/`touch` cannot see this: an Instagram WebView
        // and Chrome on the same phone report the identical viewport, and one
        // of them can complete a hosted payment while the other cannot.
        ...inAppContext(inApp),
      });
    }
    // Replay is sampled at 10%. A fortnight holds ~67 checkout_started events
    // across ~38 identities against ~27k sessions, so a flat sample would catch
    // a handful of them. This is the step where most purchase intent is lost,
    // so opt the session back in to full recording the moment it becomes one.
    phStartRecording();
    return metaEventId;
  };

  /** This card's own plan — the common case. */
  const fireBeginCheckout = (
    flow: CheckoutFlow,
    opts: {
      provider?: 'payme' | 'click';
      metaEventId?: string;
      via?: 'guest';
      deferMeta?: boolean;
    } = {},
  ): string => fireBeginCheckoutFor(plan, flow, opts);

  /**
   * The checkouts whose deferred Meta InitiateCheckout has already been fired.
   *
   * A ref, not state: firing must not render, and the second qualified gesture
   * of the SAME checkout (type a digit, then press pay; press pay, come back,
   * press again) must find the id already in here. Keyed by `metaEventId`, which
   * is the checkout — so the card sheet and the Octobank sheet, which share the
   * subscribe id, also share this guard.
   */
  const deferredMetaIcFired = useRef(new Set<string>());

  /**
   * A GUEST reached a qualified action — send Meta the pair that the sheet's
   * opening deliberately did not.
   *
   * The three qualified actions, all "this buyer is paying now" rather than
   * "this buyer is looking":
   *   1. the first digit in the card NUMBER field of the guest card sheet;
   *   2. the press that leaves for a hosted one-time provider (the prepared
   *      «Оплатить разово» link, and the create-then-navigate fallback);
   *   3. «Продолжить на Octobank» (prepared anchor and fallback alike).
   *
   * Same `metaEventId` as `checkout_opened`, so the Pixel and CAPI legs dedup
   * against each other and against the server's own IC wherever one still
   * fires. Same value rules as `fireBeginCheckoutFor` — a subscription is worth
   * `priceUzs`, a single payment the +10% `priceUzsOneTime` — because a guest's
   * IC must be comparable with a member's, not a second series.
   */
  const fireDeferredMetaIc = (
    metaEventId: string | undefined,
    flow: CheckoutFlow,
    target: Plan = plan,
  ): void => {
    // Undefined means no checkout was opened, which is not a state any caller
    // should reach — but an analytics helper must never be the thing that throws
    // inside a pay handler.
    if (!metaEventId) return;
    if (deferredMetaIcFired.current.has(metaEventId)) return;
    deferredMetaIcFired.current.add(metaEventId);
    const oneTime = flow !== 'card';
    // One value for both legs — see `fireBeginCheckoutFor`.
    const paymentType = paymentTypeFor(target.billingPeriod, flow);
    trackInitiateCheckout(metaEventId, {
      value: oneTime ? Number(target.priceUzsOneTime ?? target.priceUzs) : Number(target.priceUzs),
      currency: countryConfig.currency.code,
      planKey: target.key,
      paymentType,
    });
    /*
     * …and the CAPI leg, which for a guest is NOT gated on `isAuthed`.
     *
     * By the qualified gesture the silent `/auth/start` has usually already
     * handed back a checkout token, and `api.ts` lets that token ride on
     * `/meta/events` (and nowhere else new), so the server resolves the soft
     * account and attaches its hashed email. Without a token the endpoint
     * simply forwards nothing and answers `{accepted:false}` — the Pixel leg
     * above still stands, exactly as a guest's IC did before this change.
     */
    sendMetaEvent('InitiateCheckout', metaEventId, target.key, undefined, oneTime, paymentType);
  };

  /**
   * The sheet openings whose AddPaymentInfo has already been sent. Keyed by
   * `metaEventId` — the checkout — so the card sheet and the Octobank sheet,
   * which share the subscribe id, count as ONE opening, and a second digit, a
   * gateway swap or a second press cannot send a second event.
   */
  const addPaymentInfoFired = useRef(new Set<string>());

  /**
   * A buyer reached a qualified action — the first digit of a card number, the
   * press that leaves for a hosted provider, «Продолжить на Octobank».
   *
   * MEMBERS TOO, and that is the point of it. A member's InitiateCheckout still
   * fires when the sheet OPENS (`fireBeginCheckout('card')` on «Подписаться»,
   * `openOnceSheet` for the one-time sheet), and 16–22.09 that was 388 openings
   * by 145 people against 54 typed card numbers, 21 prepared pay-link clicks,
   * 122 `checkout_started` and 32 payments. So the event Meta optimises on is
   * mostly "opened a sheet", while this one is "started paying" — the column
   * the campaign can be moved onto once it carries the volume. IC itself is
   * deliberately untouched: the running CBO campaign needs its volume.
   *
   * Same `metaEventId` as that checkout's IC, and its own dedup set rather than
   * `deferredMetaIcFired` — the two events fire at the same gesture but are not
   * the same event, and a guest sends BOTH. Meta never dedups across event
   * NAMES, so this collapses only against its own CAPI copy.
   *
   * Same value rules as `fireDeferredMetaIc`: a subscription is worth
   * `priceUzs`, a single payment the +10% `priceUzsOneTime`.
   */
  const fireAddPaymentInfo = (
    metaEventId: string | undefined,
    flow: CheckoutFlow,
    target: Plan = plan,
  ): void => {
    // Never the thing that throws inside a pay handler — see `fireDeferredMetaIc`.
    if (!metaEventId) return;
    if (addPaymentInfoFired.current.has(metaEventId)) return;
    addPaymentInfoFired.current.add(metaEventId);
    const oneTime = flow !== 'card';
    // One value for both legs — see `fireBeginCheckoutFor`.
    const paymentType = paymentTypeFor(target.billingPeriod, flow);
    trackAddPaymentInfo(metaEventId, {
      value: oneTime ? Number(target.priceUzsOneTime ?? target.priceUzs) : Number(target.priceUzs),
      currency: countryConfig.currency.code,
      planKey: target.key,
      paymentType,
    });
    /*
     * …and the CAPI leg, NOT gated on `isAuthed`, for the same reason the
     * deferred IC above is not: a guest's silent `/auth/start` has usually
     * handed back a checkout token by the qualified gesture, and where it has
     * not, the server forwards nothing and answers `{accepted:false}` — the
     * Pixel leg above still stands.
     */
    sendMetaEvent('AddPaymentInfo', metaEventId, target.key, undefined, oneTime, paymentType);
  };

  // The ИНН fence, surfaced where it is hit.
  //
  // `ip-monthly` is the only offered plan with `requiresTin`, and it is the
  // cheapest paid tier — so the buyer most likely to lack an ИНН is the one
  // most likely to want it. The server refuses with `tin_required`, and until
  // now the page rendered that refusal's RAW English message («ИНН required
  // for this plan») to a Russian- or Uzbek-speaking user, with no control
  // anywhere on the page to satisfy it. The purchase simply ended.
  //
  // The ask is one modal we already have, so it opens here instead.
  const [tinNeeded, setTinNeeded] = useState(false);

  const checkout = useMutation({
    mutationFn: ({
      provider,
      metaEventId,
      source,
      via,
    }: {
      provider: string;
      metaEventId: string;
      source: string;
      /** `'guest'` suppresses the SERVER-side IC — the browser has just fired
       *  the deduped pair on the press. See `startCheckout`. */
      via?: 'guest';
    }) => startCheckout(plan.key, provider, metaEventId, source, via),
    onSuccess: async (result, variables) => {
      /*
       * GUEST: the order exists and the browser is about to leave for a hosted
       * provider page — take the session first.
       *
       * Awaited BEFORE the beacon delay and before anything below navigates:
       * both endings of this handler end this document (an `intent://` hand-off
       * to Chrome, or a plain `location.assign`), and the cookie has to be
       * written while the page is still here. The Instagram sequence underneath
       * is untouched — `openExternally`, then `watchHandoff`, in that order.
       *
       * A no-op for a signed-in buyer and for the yearly/fallback CTAs, which
       * hold no checkout token. Never throws — see `GuestIdentify.commit`.
       */
      await onceIdentify.commit();
      // Yield one macrotask before leaving the SPA. `fbq` and `gtag` queue their
      // network calls asynchronously, so assigning `location` on the same tick
      // routinely tore the (click-time) beacon down before it left the browser.
      // A ~50ms delay is imperceptible next to a full page navigation.
      //
      // Deliberately time-boxed rather than awaiting a callback: payment must
      // NEVER wait on analytics, so if the beacon is slow or blocked the user
      // still leaves on schedule.
      window.setTimeout(() => {
        /*
         * INSIDE INSTAGRAM ON ANDROID, hand the provider page to Chrome instead
         * of navigating this WebView.
         *
         * Measured 2026-09-19: 0 of 16 one-time orders started in that browser
         * since 06.09 completed, against 25 of 59 on desktop — the WebView
         * cannot finish the bank's 3-DS step and cannot open the Click app, so
         * the buyer lands on a page that can never say "paid". The order is
         * already created server-side at this point, and the provider returns
         * to /billing/return in whichever browser completed it, so nothing is
         * lost by finishing the payment elsewhere.
         *
         * Everything else — desktop, ordinary mobile browsers, Instagram on
         * iOS, and the CARD subscription flow (which works in Instagram:
         * 3 of 7) — keeps the plain navigation.
         *
         * `openExternally` reports false when the redirect is not an absolute
         * https URL it can express as an intent; the fall-through then behaves
         * exactly as before rather than leaving the click doing nothing.
         */
        if (needsExternalHandoff(inApp) && openExternally(result.redirectUrl)) {
          /*
           * Armed AFTER the intent, not before: the watcher measures what the
           * WebView did with it. A previous attempt's watcher is cancelled
           * first, so one checkout can never produce two verdicts and a retry
           * gets a clean 2.5 s of its own.
           */
          cancelHandoffWatch();
          handoffWatch.current = watchHandoff(() => {
            handoffWatch.current = null;
            /*
             * NOT a new event name — the same `checkout_failed` the rest of the
             * ladder uses, with its own `step`. The order exists and the buyer
             * did not reach the provider, which is exactly what this event
             * means everywhere else it fires.
             */
            track('checkout_failed', {
              checkoutId: onceMetaEventId,
              orderId: result.checkoutId,
              plan: plan.key,
              provider: variables.provider,
              step: 'handoff' satisfies CheckoutStep,
              reason: 'intent_ignored',
              // The watcher's verdict, stated on the row: the page never went
              // hidden, blurred or pagehid in the window (2026-10-17).
              pageHidden: false,
              ...inAppContext(inApp),
            });
            setHandoffStuck(result.redirectUrl);
            /*
             * AND THEN GO ANYWAY — the fix for the dead end this hand-off
             * created, measured 2026-09-22.
             *
             * `openExternally` returns true because it ASSIGNED an `intent://`
             * URL, not because Android resolved one. The Instagram WebView
             * swallows the scheme silently, so between 19.09 and 22.09 the
             * `return` below skipped the only navigation there was and the
             * buyer sat on /plans looking at a sheet that did nothing: 8
             * `checkout_failed{step:'handoff',reason:'intent_ignored'}` rows,
             * and Instagram/Android orders per buyer rose 1.4 -> 2.6 as they
             * tapped again. Before 19.09 the same buyer was at least carried to
             * the provider by `location.assign`.
             *
             * This runs ONLY on the ignored verdict, which `watchHandoff` only
             * reaches while this document is still visible and focused — so it
             * can never race a hand-off that actually took, and never fires
             * twice (the watcher settles once). The copy/open-in-tab block
             * above is set FIRST so it is on screen for the buyer if this
             * navigation is itself refused.
             */
            window.location.assign(result.redirectUrl);
          });
          return;
        }
        window.location.assign(result.redirectUrl);
      }, CHECKOUT_BEACON_GRACE_MS);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'tin_required') setTinNeeded(true);
    },
  });

  /*
   * KAZAKHSTAN — «Подписаться» goes straight to Stripe Checkout.
   *
   * The order is created by the same `POST /billing/checkout` every hosted
   * payment uses (`provider: 'stripe'`); the server prices it as the
   * subscription and answers Stripe's page. Begin-checkout fires on the click
   * as everywhere else — `flow: 'card'`, because what is bought is the
   * card-on-file subscription at `priceUzs` (the browser IC carries
   * `payment_type: subscription`, same as the server twin).
   */
  const [stripeError, setStripeError] = useState<string | null>(null);
  const stripeCheckout = useMutation({
    mutationFn: (metaEventId: string) =>
      startCheckout(plan.key, 'stripe', metaEventId, 'plansStripe'),
    onSuccess: (result) => {
      if (!result.redirectUrl) return;
      const url = result.redirectUrl;
      // Same beacon grace as the hosted path: let the Pixel call leave first.
      window.setTimeout(() => window.location.assign(url), CHECKOUT_BEACON_GRACE_MS);
    },
    onError: (e) => {
      const code = e instanceof ApiError ? e.code : null;
      // `tin_required` is a plan that needs the company id (БИН/ИИН on
      // Kazakhstan): say so, not «нажмите «Оплатить» ещё раз».
      setStripeError(
        code === 'already_subscribed'
          ? t('plans.stripe.alreadySubscribed')
          : code === 'tin_required'
            ? t('plans.tinRequired')
            : t('plans.checkoutLinkFailed'),
      );
    },
  });
  const startStripeCheckout = () => {
    setStripeError(null);
    if (!isAuthed) {
      // An order needs an account. Remember the plan; `postAuthPath` brings
      // the buyer back to /plans after sign-in, where one more tap pays.
      track('signup_wall_shown', { plan: plan.key, source: 'plansCheckout' });
      stashPendingCheckout({ planKey: plan.key, intent: 'pay' });
      void navigate('/login');
      return;
    }
    stripeCheckout.mutate(fireBeginCheckout('card'));
  };

  // ⚠️ `fullAccess`, NOT `categoryLimit == null`.
  //
  // This card is where the quota-as-tier inference did the most damage. ИП
  // legitimately has unlimited saved searches, so the old predicate classified
  // it as Pro and rendered Pro's ENTIRE feature list on it — advertising «Все
  // контакты заказчиков — без лимита» and IFI tenders on a 79 000 so'm plan
  // that grants five contacts a month and no IFI. That is not a leaked feature,
  // it is a false promise printed on the page a customer pays from.
  //
  // The API now ships the capability itself (`fullAccess`) precisely so this
  // page never has to guess again.
  const isPro = plan.fullAccess;
  // The middle tier: pays, but meters. Neither branch below described it — a
  // metered plan is not "Pro" and is emphatically not the free tier.
  const isMetered = !isPro && plan.contactOpenAllowance > 0;
  // The plan the signed-in user is actually on right now (no paid card matches
  // the free state, so a free user simply sees no «Текущий тариф» badge).
  const activePlan =
    user?.subscription && !user.subscription.expired ? user.subscription.plan : null;
  const isCurrentPlan = isAuthed && activePlan === plan.key;
  // Bank transfer («оплата по счёту») is offered ONLY on the yearly plan;
  // monthly Pro is Payme-only.
  const isYearly = plan.billingPeriod !== 'monthly';
  /*
   * Which of the three sheets a guest's «Оформить» opens.
   *
   * The same three a signed-in buyer can reach from this card, decided by the
   * same two facts — so a guest is never shown a payment shape the server would
   * refuse:
   *
   *   'card'    monthly with recurring on → the subscription sheet, Payme or
   *             Octobank, email above the method strip.
   *   'invoice' annual → «оплата по счёту». `POST /billing/subscribe-card`
   *             refuses an annual plan, so an email above a card form there
   *             would be a longer wall rather than a shorter one; bank transfer
   *             is how annual is actually bought (all three annual Pro sales on
   *             record settled that way).
   *   'once'    monthly during a Payme recurring outage → the hosted gateway
   *             chooser, which is where the authed card degrades too.
   *
   * ⚠️ `recurringOff` is "we ASKED and the answer was no", not `!recurringEnabled`
   * — which is also false while `/billing/methods` is in flight. A guest who
   * taps the instant the page paints means to subscribe; routing that tap to the
   * hosted one-time because a fetch had not landed would charge them 10% more
   * than the price on the button they pressed. Pending therefore reads as the
   * ordinary case (recurring on, which is prod), and the sheet's method strip
   * appears by itself when the answer arrives.
   */
  const recurringOff = methodsData != null && !methodsData.recurringEnabled;
  const guestTarget: 'card' | 'invoice' | 'once' = isYearly
    ? 'invoice'
    : recurringOff
      ? 'once'
      : 'card';

  // Redrawn 2-tier line: reading a tender is FREE for everyone — the full
  // description, spec and requirements («Разбор и требования ТЗ»). The paywall is
  // on ACTING on it: Pro unlocks all contacts, documents, IFI tenders, analytics
  // and unlimited saved searches. Summary/procedure are NOT sold here — they're
  // free (the page lead says so), so listing them as Pro perks would
  // misrepresent the tier.
  //
  // GROUPED lists, no parity ✕ rows (2026-09-06). The parity grid repeated the
  // same twelve lines on every card and greyed half of them out; the Pro card
  // now carries the list once, in three groups, and a seat-bearing («Бизнес»)
  // card lists ONLY what it adds on top of Pro. Membership is still derived from
  // the plan's REAL capability flags — never a hardcoded per-tier list — so a
  // card can't drift into a false promise (the trap the old per-tier lists
  // guarded against): a full-access plan without `exportEnabled` simply has no
  // export line.
  const isTeam = plan.seatLimit > 1;
  const featureGroups: { title: string | null; plus?: boolean; items: string[] }[] = !isPro
    ? [
        {
          // The metered (ИП) tier — a flat list of exactly what it grants.
          title: null,
          items: [
            plan.maxTenderBudgetUzs
              ? t('plans.feature.budgetCeiling', {
                  amount: Math.round(Number(plan.maxTenderBudgetUzs) / 1_000_000),
                })
              : t('plans.feature.allTenders'),
            ...(isMetered
              ? [
                  t('plans.feature.contactsMetered', {
                    count: plan.contactOpenAllowance,
                    word: contactsWord(plan.contactOpenAllowance, locale),
                  }),
                ]
              : []),
            t('plans.feature.savedSearchAll'),
            t('plans.feature.aiSummary'),
          ],
        },
      ]
    : isTeam
      ? [
          {
            title: t('plans.business.plus'),
            plus: true,
            items: [
              // Data-driven seat count, so raising seatLimit can't leave stale copy.
              t('plans.business.seats', { count: plan.seatLimit }),
              t('plans.business.telegram'),
              t('plans.feature.prioritySupport'),
            ],
          },
        ]
      : [
          {
            title: t('plans.group.search'),
            items: [
              t('plans.feature.allTenders'),
              t('plans.feature.privateTenders'),
              t('plans.feature.ifi'),
              t('plans.feature.savedSearchAll'),
              t('plans.feature.supplierProfile'),
              // Only where the deployment runs a bot (`messaging.telegram`) — both
              // UZ and KZ (@kaztender_com_bot) since 2026-10-14.
              ...(countryConfig.messaging.telegram ? [t('plans.feature.telegramBot')] : []),
            ],
          },
          {
            title: t('plans.group.contacts'),
            items: [
              t('plans.feature.contactsAll'),
              // Winner phone/email (2026-10-06) — full-access only on the API,
              // so it belongs to this list and never to the ИП one above. UZ
              // only: the phones come from the UZ registry import; KZ has no
              // such data, and the bullet would be a false promise there.
              ...(countryConfig.code === 'uz' ? [t('plans.feature.winnerContacts')] : []),
              t('plans.feature.documents'),
            ],
          },
          {
            title: t('plans.group.analysis'),
            items: [
              t('plans.feature.buyerAnalytics'),
              ...(plan.analyticsEnabled ? [t('plans.feature.analytics')] : []),
              // Org-watch (named buyers/competitors for new AWARDS) is Pro-gated server-side.
              t('plans.feature.orgWatch'),
              ...(plan.exportEnabled ? [t('plans.feature.export')] : []),
              // Priority support is a yearly bonus on Pro (Business carries it on
              // every period, see the `plus` list above).
              ...(isYearly ? [t('plans.feature.prioritySupport')] : []),
            ],
          },
        ];

  // The DB stores one Russian label per plan; the Uzbek page was rendering it
  // verbatim («ИП — oy», «Бизнесga obuna boʻlish»).
  const planName = planDisplayName(plan, t);

  /** «399 000 сум» — one place that decides how an amount is written, so the
   *  card, the modal and the bank-transfer block cannot format it differently. */
  const som = (amount: number | string) => `${formatBudget(String(amount))} ${t('plans.som')}`;

  // No `amountLabel` for this card's own subscription price any more: the modal
  // quotes whichever plan is actually being charged (it can switch to the year
  // mid-flow), and the bank-transfer block quotes the one-time price. A binding
  // for "this card's monthly price" had no consumer left.
  /*
   * The single-payment price — the API's number, never a percentage applied
   * here. The card must quote exactly what /billing/checkout will charge.
   *
   * ⚠️ `null` when the API did not send one, and every consumer below has to
   * cope rather than interpolate it. Seen for real against a local API one
   * deploy behind: the button rendered «Разовый платёж — null сум» and the note
   * «Экономия null сум». That is not a local-only hazard — web and API ship on
   * SEPARATE pipelines gated by `changes:` filters, so a merge touching only
   * `apps/web/**` reaches production without the API that supplies this field.
   *
   * The fallback is to quote NO price, not a computed one. Re-deriving it here
   * as `priceUzs × 1.1` would put a number on the button that the server never
   * agreed to charge, and the moment the surcharge changes on one side the page
   * advertises one price and bills another. A button that says «Разовый платёж»
   * and reveals the amount at checkout is honest; a confident wrong number on
   * the page where someone decides whether to trust us is not.
   */
  const oneTimeUzs = plan.priceUzsOneTime == null ? null : Number(plan.priceUzsOneTime);
  const oneTimeLabel = oneTimeUzs == null ? null : som(oneTimeUzs);
  /*
   * The figure printed on the invoice sheet BEFORE the счёт is issued.
   *
   * `priceUzsOneTime` is the authority and is used whenever it is there. The
   * fallback is safe here and only here: the +10% one-time surcharge is
   * MONTHLY-only — the API returns the base price for an annual plan — and this
   * sheet is annual-only. So `priceUzs` is not a locally recomputed price, it is
   * the same server figure under another name, which is exactly what the "quote
   * nothing rather than guess" rule on the monthly buttons is protecting. The
   * issued invoice then reprints the server's own `amountUzs` regardless, so
   * this line can never become the amount anyone is charged.
   */
  const invoiceAmountLabel = oneTimeLabel ?? som(Number(plan.priceUzs));
  // One-time access ends one calendar month from today — the same arithmetic
  // the API applies on completion (`periodEnd.setMonth(+1)`), so the footnote
  // names the date the account will actually show.
  const onceEndsAt = DateTime.now().setZone('Asia/Tashkent').plus({ months: 1 });
  const onceUntil = formatDeadline(onceEndsAt.toISODate(), locale) ?? '';
  // Whole days the payment buys — a calendar month is 28–31, so print the real
  // number instead of a rounded "30 дней".
  const onceDays = Math.max(
    1,
    Math.round(onceEndsAt.diff(DateTime.now().setZone('Asia/Tashkent'), 'days').days),
  );
  // The card's headline is the SUBSCRIPTION price. The +10% one-time figure is
  // printed on its own button and repeated inside the gateway sheet («К оплате:
  // 439 000 сум за 1 месяц»), so a buyer who read 399 000 is never charged
  // 439 000 without having seen that number twice.

  // Fire a one-time hosted checkout for the chosen gateway. Shared by every
  // pay-once path so the advisory ИНН gate + begin-checkout beacon stay identical
  // across Payme and Click. (The server enforces the ИНН requirement regardless.)
  const fireOneTime = async (provider: 'payme' | 'click', source: string) => {
    if (plan.requiresTin && !user?.tin) {
      setTinNeeded(true);
      return;
    }
    /*
     * GUEST: the address on the sheet buys the account, then the gateway is
     * called — `POST /billing/checkout` has nowhere to hang an order otherwise
     * (`payment_orders.user_id` is NOT NULL). Resolves true with no request for
     * a signed-in buyer, so the authed path below is untouched.
     *
     * The buttons are NOT disabled while the address is missing — `ensure()`
     * marks the field and focuses it instead, and returns false, so the tap
     * costs a highlight rather than a redirect. A SECOND attempt after a failed
     * redirect finds `active` false and goes straight through: the account
     * exists and nothing asks for the address again.
     */
    /*
     * GUEST, INSIDE THE SHEET, NOT YET IDENTIFIED: this press only identifies.
     *
     * Until `/auth/start` has answered there is no prepared order, so the
     * control is still the plain button — and its old ending, create-then-
     * `location.assign` from an async callback, is exactly the navigation an
     * in-app WebView drops in silence. That is the window between the last
     * keystroke and the debounced identification (600 ms plus one round trip):
     * the buyer who types the address and taps at once. So: identify now (the
     * same call the debounce would have made), then RETURN. Identified →
     * `canPrepare` → the hook creates the order → the control shows «Готовим
     * оплату…» and then the real link, and the next tap is a user gesture on an
     * `<a href>`. One extra tap in a rare race, no dead end.
     *
     * `ensure()` returning false means a code/password stage was revealed — the
     * next press re-enters it, exactly as before. Outside the sheet (yearly
     * card, recurring outage) nothing is prepared, so the old path stands.
     */
    if (onceOpen && onceAsGuest && !onceIdentify.identified) {
      await onceIdentify.ensure();
      return;
    }
    if (!(await onceIdentify.ensure())) return;
    // Inside the sheet the Pixel/CAPI leg already fired on open for a MEMBER
    // (see `openOnceSheet`); the pick joins that attempt under the same id. The
    // yearly card and the recurring-outage fallback have no sheet, so there the
    // id is undefined and the whole beacon set fires on this click as before.
    const metaEventId = fireBeginCheckout('hosted', {
      provider,
      metaEventId: onceMetaEventId,
      // Guest one-time: same event, one extra key. Read from the flag pinned
      // when the sheet opened — `isAuthed` has already flipped by now.
      ...(onceAsGuest ? { via: 'guest' as const, deferMeta: true } : {}),
    });
    /*
     * QUALIFIED ACTION #2, on the create-then-navigate fallback — the same press
     * as the prepared link, for a sheet whose background order never landed.
     * Fired BEFORE the mutation, so the beacon leaves while this page is
     * certainly still here: `checkout.onSuccess` ends this document.
     */
    if (onceAsGuest) fireDeferredMetaIc(metaEventId, 'hosted');
    // …and AddPaymentInfo for EVERYONE on the same press: for a member this is
    // the first gesture in the whole one-time flow that is not "looking", since
    // their IC already fired when the sheet opened.
    fireAddPaymentInfo(metaEventId, 'hosted');
    checkout.mutate({
      provider,
      metaEventId,
      source,
      ...(onceAsGuest ? { via: 'guest' as const } : {}),
    });
  };

  /**
   * Open the card-subscription modal for THIS plan.
   *
   * Extracted from the «Подписаться» button so the `?plan=` deep link below can
   * reach exactly the same path — same ИНН pre-check, same begin-checkout
   * beacon, same gateway choice. A second, parallel opener would be a second
   * place for the funnel ladder to diverge.
   *
   * ⚠️ Ask for the ИНН BEFORE the card, not after the OTP — the server refuses
   * inside `subscribe-card`, which runs only once the card is tokenized AND the
   * SMS verified, and the modal reported that refusal as «неверный код».
   * Advisory only; the server still enforces it.
   */
  const openSubscribeModal = () => {
    if (plan.requiresTin && !user?.tin) {
      setTinNeeded(true);
      return;
    }
    setSubscribePlan(plan);
    setSubscribeMetaEventId(fireBeginCheckout('card'));
    // Single CTA, backend picks the gateway: OCTO-allowlisted users go to the
    // Octobank redirect modal; everyone else to the Payme inline card modal.
    // Same label, same disclosure, honest strip.
    if (octoGateway) {
      setPayMethod('payme'); // default to Payme; Octobank is an option
      setMethodSwap(false); // a fresh checkout, so the sheet makes its entrance
      setOctoOpen(true);
    } else setSubscribeOpen(true);
  };

  /*
   * The identify→resume hand-off is GONE with the sheet that needed it. It
   * existed because identification happened on a DIFFERENT sheet and the page
   * then had to work out, once `/billing/methods` had landed, which checkout to
   * re-open. Nothing re-opens now: all three guest paths identify INSIDE the
   * sheet that is already taking the money, so there is nothing to resume.
   */

  /*
   * `?plan=<key>` — the far end of the sign-in round trip.
   *
   * A guest who picked a plan and was sent to /login came back to '/' with the
   * pick forgotten (see `postAuthPath`); now they come back HERE, on the plan
   * they chose, with the card modal already open. Measured 2026-09-18 over 30
   * days: 245 guests hit the «войдите» wall and none of them reached checkout.
   *
   * Deliberately opens only the CARD modal, never a hosted redirect: a modal
   * asks for a confirmation, a redirect is most of the way to a charge, and a
   * URL must not be able to start one. Same reason it is gated on `ctaReady` —
   * the fast-click race this page already guards (see `authKnown`) would
   * otherwise fire the moment auth resolves, on stale state.
   *
   * `resumedCheckout` makes it once-per-mount: closing the modal must not
   * re-open it on the next render, and the param survives in the address bar.
   */
  const resumedCheckout = useRef(false);
  const planParam = searchParams.get('plan');
  useEffect(() => {
    if (resumedCheckout.current) return;
    if (!isSafeCheckoutToken(planParam) || planParam !== plan.key) return;
    if (!ctaReady || !isAuthed || isCurrentPlan) return;
    // Kazakhstan has no card sheet to open — and a URL must not start a redirect.
    if (STRIPE_CHECKOUT || !recurringEnabled || isYearly) return;
    resumedCheckout.current = true;
    openSubscribeModal();
    // `openSubscribeModal` is deliberately NOT a dependency: it is re-created
    // every render, and the ref guard above is what makes this run once.
  }, [planParam, ctaReady, isAuthed, isCurrentPlan, recurringEnabled, isYearly, plan.key]);

  // «Оплатить разово» — opens the gateway chooser. Its own first-party event,
  // because the hosted `checkout_opened` fires only on the gateway pick inside
  // the sheet: without this, "opened the sheet and left" and "never wanted to
  // pay once" would write the same row. ⚠️ Discontinuity for anyone comparing
  // one-time intent across 2026-09-06: before, the button itself fired
  // `checkout_opened{flow:'hosted'}`; after, that fires on the pick, and the
  // button-level denominator is THIS event.
  //
  // Meta/GA4 InitiateCheckout fires HERE (with the one-time amount), not on the
  // pick — symmetric with the subscribe button, which fires before its modal —
  // so the IC series keeps counting button clicks and Meta's optimiser is not
  // starved by a second step. Same advisory ИНН gate as the buttons inside.
  const openOnceSheet = () => {
    if (plan.requiresTin && !user?.tin) {
      setTinNeeded(true);
      return;
    }
    const metaEventId = uuid();
    /*
     * ⚠️ MEMBERS ONLY, since 2026-09-22.
     *
     * For a signed-in buyer this is unchanged — IC on the button, with the
     * one-time amount, symmetric with the subscribe button. For a GUEST both
     * legs are held back until the press that actually leaves for the provider
     * (`onPreparedPayClick` / `fireOneTime` → `fireDeferredMetaIc`), because a
     * guest with no account can open this sheet out of curiosity: 1 of 31 such
     * opens converted on 19–21.09 against 11 of 86 for members, and Meta's CBO
     * campaign optimises on this exact event.
     *
     * `pay_once_opened` below is NOT held back — it is this button's own
     * denominator and the registry documents it.
     */
    if (isAuthed) {
      // The one-time sheet is the hosted flow → `onetime` on a monthly plan. One
      // value, handed to both legs.
      const paymentType = paymentTypeFor(plan.billingPeriod, 'hosted');
      trackInitiateCheckout(metaEventId, {
        value: Number(plan.priceUzsOneTime ?? plan.priceUzs),
        currency: countryConfig.currency.code,
        planKey: plan.key,
        paymentType,
      });
      // oneTime=true: this is the «Оплатить разово» sheet, so the CAPI leg must be
      // valued at the +10% one-time price to match this Pixel fire (priceUzsOneTime).
      sendMetaEvent('InitiateCheckout', metaEventId, plan.key, undefined, true, paymentType);
    }
    // Opt the session into full replay from the moment intent is shown — a
    // buyer who opens the sheet and leaves is exactly the drop this exists to see.
    phStartRecording();
    setOnceMetaEventId(metaEventId);
    setOnceAsGuest(!isAuthed);
    track('pay_once_opened', {
      plan: plan.key,
      checkoutId: metaEventId,
      ...deviceContext(),
      ...inAppContext(inApp),
      /*
       * `handoff: 'external'` marks the attempts that will leave for Chrome
       * rather than navigating this browser — decided here, because the
       * condition is the browser, not the redirect URL, and this event is the
       * denominator the hand-off has to be judged against.
       *
       * It rides on THIS event rather than `checkout_started`, which is written
       * server-side and cannot see the browser's decision. The yearly card has
       * no sheet and so no row here; there the same population is
       * `checkout_opened{flow:'hosted', inApp:'instagram', inAppOs:'android'}`.
       */
      ...(needsExternalHandoff(inApp) ? { handoff: 'external' } : {}),
    });
    setOnceOpen(true);
  };

  /*
   * Card-via-app one-time gateways, IN THE ORDER THEY ARE SHOWN. Payme is
   * always offered; Click joins it when the `click` gateway is enabled
   * (PAYMENT_METHODS lists it). The first entry is the one the sheet pre-picks
   * and prepares an order for as it opens (`preparingFor` below), and the
   * yearly card lays its two buttons out in the same order.
   *
   * ⚠️ THE ORDER DEPENDS ON THE DEVICE. On a touch screen Click leads; with a
   * mouse Payme stays first. Owner's call, 2026-09-25 («клик сделать первым»),
   * on these numbers from prod, 13–25.09: of 43 phone buyers who reached
   * Payme's hosted page from this sheet, 0 started a transaction and 41 came
   * back with the Back button; Click's hosted page converted 4 of 12 phone
   * buyers in the same window, and Payme still converted 8 of 18 on desktop.
   * Why phones stall on Payme's page is NOT established — the owner disputes
   * that the page changed — so nothing here says why, nothing is hidden, and
   * Payme remains one tap away. The buyer simply finds the gateway that works
   * on his kind of device already selected.
   *
   * `useCoarsePointer` is hydration-safe (server says `false`), which matters
   * because /plans is prerendered and the yearly card's buttons are in that
   * markup; a phone sees Payme→Click for one frame and then the swap. The value
   * is read ONCE for the whole card, above — the «Оплатить с компьютера» guard
   * needs the same answer, and two calls would be two sources of truth.
   */
  const paymeGateway = {
    m: 'payme' as const,
    logo: '/img/payme.png',
    ariaKey: 'plans.payOnceViaPayme',
    source: 'plansOneTime',
  };
  const clickGatewayTile = {
    m: 'click' as const,
    logo: '/img/click.svg',
    ariaKey: 'plans.payWithClick',
    source: 'plansClick',
  };
  const payOnceGateways = (
    coarsePointer ? [clickGatewayTile, paymeGateway] : [paymeGateway, clickGatewayTile]
  ).filter((g) => g.m === 'payme' || clickGateway);
  /**
   * The gateway the order is being prepared for.
   *
   * ⚠️ The FIRST gateway is picked by default, so the order starts preparing as
   * the sheet opens and the pay control is a link by the time the buyer reaches
   * it. Waiting for an explicit pick would cost a tap and, for the ~1 buyer in 2
   * who never touches the strip, would leave the pay control on the
   * create-then-navigate path this whole change exists to get off.
   *
   * The cost is one `pending` order per opened sheet — and a second one for a
   * buyer who switches to Click. That is what the abandoned-checkout sweep's
   * `never_opened` filter is for; without it every opened sheet would send a
   * «🛒 Брошено» alert.
   */
  const preparingFor = onceOpen ? (onceGateway ?? payOnceGateways[0]?.m ?? null) : null;
  /*
   * A guest is billable only once `/auth/start` has answered — that is what
   * mints the checkout token `payment_orders.user_id` needs. `identified` is
   * that token existing; for a signed-in buyer it is simply true.
   */
  const canPrepare = isAuthed || onceIdentify.identified;
  /*
   * GUEST: take the silent identification step as soon as the address is
   * well-formed, so the order can be prepared before the pay gesture rather
   * than during it.
   *
   * ⚠️ This is the ONE place the sheet reaches the network without being
   * pressed, and it is debounced for that reason: `/auth/start` on an address
   * that already has an account reveals the code field and mails a code, and
   * doing that per keystroke would mail a code to every typo that happens to be
   * someone's address. `ready` is the hook's own client-side syntax check, so
   * nothing leaves the browser for «ivan@» at all. If the answer is
   * code/password the sheet simply keeps its ordinary button, which is exactly
   * what the buyer had before — `ensure()` is idempotent and their press
   * re-enters it at the revealed stage.
   */
  useEffect(() => {
    if (!onceOpen || !preparingFor) return;
    if (isAuthed || onceIdentify.identified || !onceIdentify.ready) return;
    const id = window.setTimeout(() => void onceIdentify.ensure(), GUEST_PREPARE_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
    // `onceIdentify` is rebuilt every render; only its primitives may be deps.
    // ⚠️ `email` IS one of them: `ready` stays true from the first well-formed
    // keystroke on, so without the string here the timer armed on «…gmail.co»
    // never restarts for the «m», and the `ensure()` it fires is the one that
    // captured the shorter address (guest-checkout-identify.tsx, `email`).
  }, [
    onceOpen,
    preparingFor,
    isAuthed,
    onceIdentify.identified,
    onceIdentify.ready,
    onceIdentify.email,
  ]);

  /*
   * The address block stays on THIS open sheet once it has been on it — the same
   * pin, for the same reason, as `identifyPinned` in the Octobank sheet: since
   * the prepared pay link (2026-10-01) `commit()` lands while the buyer is
   * reaching for the link, and dropping the focused field there takes the focus
   * — and with it the sheet's lift above the keyboard — at that moment. Read
   * from the code; the tap lost on prod on 2026-10-02 was a different trigger of
   * the same jump (see the note there). Released when the sheet closes.
   */
  const [onceIdentifyPinned, setOnceIdentifyPinned] = useState(false);
  const onceIdentifyActive = onceIdentify.active;
  useEffect(() => {
    if (!onceOpen) setOnceIdentifyPinned(false);
    else if (onceIdentifyActive) setOnceIdentifyPinned(true);
  }, [onceOpen, onceIdentifyActive]);

  const prepared = usePreparedCheckout({
    enabled: onceOpen,
    gateway: preparingFor,
    identified: canPrepare,
    /*
     * A LINK, not an order (2026-10-01, owner: «заказ больше не надо заранее
     * создавать»). The server checks what the order would need and answers a
     * URL on our own API; the order — and the server's `checkout_started` and
     * InitiateCheckout — is created only when the buyer follows it. The anchor
     * below is exactly as real a link as before, so the tap is still the user
     * gesture that fixed 22.09. See `prepareCheckoutLink`.
     */
    create: (gateway) =>
      prepareCheckoutLink(
        'hosted',
        plan.key,
        gateway,
        // The SAME id the sheet's Pixel/CAPI InitiateCheckout used, so the
        // order the tap creates joins that attempt rather than opening a second.
        onceMetaEventId ?? uuid(),
        gateway === 'click' ? 'plansClick' : 'plansOneTime',
        /*
         * `via:'guest'` rides with the stored request: a guest's server-side
         * InitiateCheckout stays skipped when the tap creates the order, exactly
         * as before — the browser fires that buyer's pair on the press itself.
         */
        onceAsGuest ? 'guest' : undefined,
      ),
    onReady: (order) => {
      /*
       * GUEST: take the session NOW, not on the anchor's click.
       *
       * `commit()` is async and an anchor navigation cannot be awaited, so the
       * cookie has to be written while the page is certainly still here. The
       * order exists and its provider page is one tap away, which is as close
       * to "the money has moved" as a redirect flow gets before it leaves.
       * Never throws — see `GuestIdentify.commit`. No-op for a member.
       */
      void onceIdentify.commit();
      track('checkout_handoff_shown', {
        checkoutId: onceMetaEventId,
        orderId: order.orderId,
        plan: plan.key,
        provider: order.gateway,
        ...deviceContext(),
        ...inAppContext(inApp),
      });
    },
  });

  /** Close the sheet and forget its attempt — a stale error must not greet the next open. */
  const closeOnceSheet = () => {
    setOnceOpen(false);
    setOnceMetaEventId(undefined);
    setOnceAsGuest(false);
    setOnceGateway(null);
    // The prepared order is forgotten with the sheet: the next open mints a new
    // `checkoutId`, and re-using an order created under the previous one would
    // silently split that attempt's Pixel/CAPI legs from its payment.
    prepared.reset();
    // A stale «не открылось?» must not greet the next attempt, and a watcher
    // armed for a sheet nobody is looking at must not reach its deadline.
    cancelHandoffWatch();
    setHandoffStuck(null);
    setHandoffCopied(false);
    checkout.reset();
  };
  // Android «Назад» closes the one-time sheet and the bank-transfer sheet
  // instead of leaving /plans (Igor, 10.10, Redmi). The card and Octobank
  // sheets register themselves; all of them share ONE history entry.
  useBackClosesSheet(onceOpen, closeOnceSheet);
  useBackClosesSheet(showBank, () => setShowBank(false));

  /*
   * One line, only inside Instagram on Android, saying where the payment page
   * will open — because it opens somewhere else than the tap suggests, and an
   * app switching to Chrome unannounced reads as something having gone wrong.
   * No modal and no extra step: the button does the same thing it always did.
   */
  const externalHint = needsExternalHandoff(inApp) ? (
    <p
      data-testid="once-external-hint"
      className="mt-2.5 text-center text-[12px] leading-snug text-ink-muted"
    >
      {t('plans.onceExternalHint')}
    </p>
  ) : null;

  // «Оплатить картой через приложение» — one outlined button per gateway (logo +
  // chevron), each a one-time hosted redirect. Shown on the yearly card and in
  // the monthly «Разово» tab.
  const appPayButtons = (
    <>
      <div className={`grid gap-2 ${payOnceGateways.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {payOnceGateways.map(({ m, logo, ariaKey, source }) => (
          <button
            key={m}
            type="button"
            disabled={checkout.isPending}
            onClick={() => void fireOneTime(m, source)}
            aria-label={t(ariaKey)}
            className="inline-flex h-[46px] items-center justify-between gap-2 rounded-md border-[1.5px] border-edge bg-bone-surface px-3.5 transition-colors hover:border-brand hover:bg-brand-dim disabled:pointer-events-none disabled:opacity-60"
          >
            <img src={logo} alt="" className="h-[18px] w-auto" />
            <ChevronRightGlyph />
          </button>
        ))}
      </div>
      {/* The yearly card pays once too, and has no sheet to carry the note. */}
      {externalHint}
    </>
  );

  // Footnote under the CTAs: who processes the money. Payme and Octobank are
  // always named — both really do process our charges (Octobank takes the
  // Visa/Mastercard subscriptions and is named in the trust strip too), even
  // though the Octobank gateway is offered inside the modal only to allowlisted
  // users. Click is named only when its gateway is enabled.
  //
  // Since 2026-10-11 the refund window leads it (owner): «7 дней — гарантия
  // возврата средств», directly UNDER the buttons and above the processors —
  // under, not over, so nothing sits between the feature list and the action.
  // Same sentence and glyph as the headline of the payment sheet the button
  // opens (`billing.card.refund7`).
  const paymentFootnote = (
    <>
      <p
        data-testid="plan-refund-line"
        className="mt-3 flex items-center justify-center gap-1.5 text-center text-[13.5px] font-bold leading-snug text-ink"
      >
        <svg
          viewBox="0 0 24 24"
          className="h-4 w-4 shrink-0 text-brand"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M3 12a9 9 0 1 0 3-6.7" />
          <path d="M3 4v5h5" />
        </svg>
        <span>{t('billing.card.refund7')}</span>
      </p>
      <div className="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-[12px] leading-none text-ink-muted">
        <span className="leading-none">{t('plans.paymentVia')}</span>
        {/* Logo heights are tuned per mark so the three read as one row: the
            Payme wordmark is tall, Click sits low, Octobank in between.
            The three marks are the UZBEK gateways, so they render on the Uzbek
            build only. Kazakhstan showed «Оплату обрабатывают: Payme · octobank»
            under every plan card (audit 29.09); there the line is words alone —
            its `plans.paymentVia` names the provider (locales/ru.kz.ts). */}
        {STRIPE_CHECKOUT && <PaymentMarks />}
        {countryConfig.code === 'uz' && (
          <span className="inline-flex items-center gap-3.5">
            <img src="/img/payme.png" alt="Payme" className="block h-[19px] w-auto" />
            {clickGateway && (
              <img src="/img/click.svg" alt="Click" className="block h-[15px] w-auto" />
            )}
            <img src="/img/octobank.svg" alt="Octobank" className="block h-[17px] w-auto" />
          </span>
        )}
      </div>
      {STRIPE_CHECKOUT && countryConfig.payments.cardPayee && (
        <p className="mt-1.5 text-center text-[11.5px] leading-snug text-ink-muted">
          {t('plans.stripe.payee', { name: countryConfig.payments.cardPayee.name })}
        </p>
      )}
    </>
  );

  /*
   * The filled primary CTA on a plan card. ONE string, used by the member's
   * «Подписаться на Pro» / «Оплатить разово» and by the guest's CTA — the guest
   * must not merely look similar, it must be the same control (Igor,
   * 2026-09-20: «одинаковые кнопки»), and two copies of a class list drift.
   */
  const primaryCtaClass =
    'flex min-h-[52px] w-full items-center justify-between gap-2 rounded-md bg-brand px-4 py-2 text-left text-[14.5px] font-bold leading-tight text-white transition-colors hover:bg-brand-mid md:min-h-[46px] md:px-3.5 md:text-[13.5px]';
  /*
   * The ONE button of a `cardOnly` card (Instagram / Telegram on Android) —
   * face and the line under it, shared by the member's and the guest's CTA so
   * the two cannot drift. It opens the same card sheet «Подписаться» does and
   * sells the same subscription at the same price; only the words change,
   * and the renewal is stated on the page before the tap, not left to the sheet.
   */
  const cardOnlyFace = (
    <>
      <span className="inline-flex min-w-0 items-center gap-1.5">
        <CardGlyph />
        <span>{t('plans.cardOnlyCta')}</span>
      </span>
      <span className="shrink-0 whitespace-nowrap text-[14px] md:text-[13.5px]">
        {t('plans.cardOnlyPrice', { amount: som(Number(plan.priceUzs)) })}
      </span>
    </>
  );
  const cardOnlyNote = (
    <p
      data-testid="card-only-note"
      className="mt-2 text-center text-[12px] leading-snug text-ink-muted"
    >
      {t('plans.cardOnlyRenews')}
    </p>
  );
  /*
   * The prepared anchor was followed.
   *
   * ⚠️ NOTHING here may `preventDefault`. The anchor's own navigation is the
   * thing that works, and everything below is additive:
   *
   *   • `checkout_handoff_link_clicked` — the numerator the abandoned-checkout
   *     sweep reads to tell "walked away from the provider" from "never went".
   *     Fire-and-forget beacon, no await.
   *   • INSIDE INSTAGRAM ON ANDROID, one extra attempt to hand the page to
   *     Chrome (`intent://`). If that WebView honours it, Chrome opens and can
   *     finish the bank's 3-DS step, which this WebView cannot; if it swallows
   *     it — as it did on every traced attempt since 19.09 — the anchor's own
   *     navigation still lands the buyer on the provider page inside the app.
   *     Either way there is no dead end, which is what 93fc956a bought and what
   *     this must not spend.
   *   • The 2.5 s watcher stays armed for TELEMETRY only: it writes
   *     `checkout_failed{step:'handoff', reason:'intent_ignored'}` and changes
   *     nothing on screen. The buyer is already on their way.
   */
  const onPreparedPayClick = (order: { url: string; orderId: string }) => {
    /*
     * `checkout_opened{flow:'hosted'}` STILL fires on the pay gesture, and this
     * is now the pay gesture. It used to ride on the gateway tile because that
     * tile both picked and paid; moving it with the payment is what keeps the
     * hosted ladder in order — `checkout_opened` before the server's
     * `checkout_started` would otherwise invert, since the order is created in
     * the background now. For a MEMBER the Pixel/CAPI InitiateCheckout legs
     * already fired on sheet open and are NOT repeated (`pixelAlreadyFired`).
     */
    const metaEventId = fireBeginCheckout('hosted', {
      provider: preparingFor ?? undefined,
      metaEventId: onceMetaEventId,
      ...(onceAsGuest ? { via: 'guest' as const, deferMeta: true } : {}),
    });
    /*
     * QUALIFIED ACTION #2 — the press that leaves for the provider. THIS is a
     * guest's InitiateCheckout, held back from the sheet's opening.
     *
     * ⚠️ Fire-and-forget, like every beacon on this handler, and it returns
     * void: nothing here may await and nothing here may `preventDefault`. The
     * anchor's own navigation is the fix this whole sheet is built around.
     */
    if (onceAsGuest) fireDeferredMetaIc(metaEventId, 'hosted');
    // Members included: their IC fired on the sheet's opening, so this press is
    // the only place their "started paying" can be observed. Fire-and-forget on
    // the same terms as the line above — nothing here may await.
    fireAddPaymentInfo(metaEventId, 'hosted');
    track('checkout_handoff_link_clicked', {
      checkoutId: onceMetaEventId,
      orderId: order.orderId,
      plan: plan.key,
      provider: preparingFor ?? undefined,
      ...deviceContext(),
      ...inAppContext(inApp),
    });
    if (!needsExternalHandoff(inApp)) return;
    if (!openExternally(order.url)) return;
    cancelHandoffWatch();
    handoffWatch.current = watchHandoff(() => {
      handoffWatch.current = null;
      track('checkout_failed', {
        checkoutId: onceMetaEventId,
        orderId: order.orderId,
        plan: plan.key,
        provider: preparingFor ?? undefined,
        step: 'handoff' satisfies CheckoutStep,
        reason: 'intent_ignored',
        pageHidden: false,
        ...inAppContext(inApp),
      });
    });
  };

  /**
   * THE ONE-TIME PAY CONTROL, in whichever of its three states applies.
   *
   * `ready`      — a real `<a href>` to the provider page. Following a link IS
   *                the user gesture, which is the entire fix: the old
   *                `location.assign` fired from an async callback and was
   *                dropped in silence by mobile engines and in-app WebViews
   *                (12 orders created, 0 completed in the 24 h to 22.09).
   * `preparing`  — the order is being created. Non-interactive with `aria-busy`,
   *                and the ONE place this sheet is allowed a dead-looking
   *                control: it lasts one round trip, it is never a validation
   *                state, and it says what it is doing.
   * anything else — exactly the button that shipped before: press → create →
   *                navigate, including every guest branch (`ensure()` marks the
   *                address rather than disabling anything). A failed
   *                preparation lands here too, with its own error line below.
   */
  const payOnceCtaClass =
    'flex min-h-[52px] w-full items-center justify-center gap-2 rounded-md bg-brand px-4 py-2 text-center text-[14.5px] font-bold leading-tight text-white transition-colors hover:bg-brand-mid';
  const payOnceLabel = oneTimeLabel
    ? t('plans.oncePayNow', { amount: oneTimeLabel })
    : t('plans.payOnceBtn');
  const readyOrder = prepared.state.status === 'ready' ? prepared.state : null;
  const payOnceCta = readyOrder ? (
    <a
      data-testid="once-pay-link"
      href={readyOrder.url}
      rel="noopener"
      onClick={() => onPreparedPayClick(readyOrder)}
      className={payOnceCtaClass}
    >
      {payOnceLabel}
    </a>
  ) : prepared.state.status === 'preparing' ? (
    <button
      type="button"
      data-testid="once-pay-preparing"
      disabled
      aria-busy="true"
      className={`${payOnceCtaClass} disabled:cursor-default disabled:opacity-70`}
    >
      <Spinner />
      {t('plans.oncePreparing')}
    </button>
  ) : (
    <button
      type="button"
      data-testid="once-pay-button"
      /* ⚠️ NEVER disabled for a missing address (Igor, 2026-09-19: «кнопка
           жмётся пусть, но подсвечивает, что не заполнено»). The press runs
           `onceIdentify.ensure()`, which marks the field and puts the caret in
           it without calling anything. Disabled only while a request is
           actually in flight. */
      disabled={checkout.isPending || onceIdentify.busy}
      onClick={() =>
        void fireOneTime(
          preparingFor ?? 'payme',
          preparingFor === 'click' ? 'plansClick' : 'plansOneTime',
        )
      }
      className={`${payOnceCtaClass} disabled:pointer-events-none disabled:opacity-60`}
    >
      {payOnceLabel}
    </button>
  );

  /*
   * «ОПЛАТИТЬ С КОМПЬЮТЕРА» (26.09–01.10) is gone: a text link under the pay
   * control that sent the prepared order to the buyer's other device. Used by
   * three people, all in Instagram on Android, and it produced no payment; that
   * browser now sells the card subscription only (`cardOnly`). Links already
   * sent still open — `/pay/:token` and its API are untouched.
   */

  /** The invoice variant — centred, no trailing price (the счёт names it). */
  const invoiceCtaClass =
    'inline-flex min-h-[46px] w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-brand px-2.5 py-2 text-center text-[13px] font-bold leading-tight text-white transition-colors hover:bg-brand-mid';

  /*
   * THE GUEST'S PRIMARY CTA — the member's button, label and all.
   *
   * It used to read «Оформить» while the signed-in buyer standing next to it
   * got «Подписаться на Pro» with the price on the right, and the yearly card
   * got «Оформить» where a member reads «Оплата по расчётному счёту». Same
   * card, same plan, same money, two different promises — and the guest's was
   * the vaguer one, on the audience that has the least context. So the face of
   * the button is decided by `guestTarget`, which already decides what it
   * OPENS: whatever sheet a member would reach from this card, the guest's
   * button is labelled and shaped like the member's button to that sheet.
   *
   * Only the FACE. The click handler below is untouched — same events, same
   * meta, same `signup_wall_shown` denominator.
   */
  const guestCta =
    guestTarget === 'invoice'
      ? {
          className: invoiceCtaClass,
          // No chevron: the member's is an accordion toggle, this one opens the
          // sheet outright, and a chevron would promise an expand that never comes.
          face: (
            <>
              <BankGlyph />
              {t('plans.payByAccount')}
            </>
          ),
        }
      : guestTarget === 'once'
        ? {
            // Recurring is off (a Payme outage). A member's card degrades to the
            // one-time buttons here, so «Подписаться» would name a product this
            // tap cannot buy.
            className: primaryCtaClass,
            face: (
              <>
                <span className="inline-flex min-w-0 items-center gap-1.5">
                  <CardGlyph />
                  <span>{t('plans.payOnceBtn')}</span>
                </span>
                {oneTimeLabel && (
                  <span className="shrink-0 whitespace-nowrap text-[14px] md:text-[13.5px]">
                    {oneTimeLabel}
                  </span>
                )}
              </>
            ),
          }
        : cardOnly
          ? // Instagram / Telegram on Android — the member's `cardOnly` button.
            { className: primaryCtaClass, face: cardOnlyFace }
          : {
              className: primaryCtaClass,
              face: (
                <>
                  <span className="inline-flex min-w-0 items-center gap-1.5">
                    <LockIcon className="shrink-0" />
                    <span>{t('plans.subscribeWithCard', { name: planName })}</span>
                  </span>
                  <span className="shrink-0 whitespace-nowrap text-[14px] md:text-[13.5px]">
                    {som(Number(plan.priceUzs))}
                  </span>
                </>
              ),
            };

  /*
   * The «Оплатить разово» gateway chooser, hoisted out of the authed branch.
   *
   * It is rendered ONCE, at the bottom of the card, outside every `isAuthed`
   * gate — because a guest now identifies INSIDE it. `/auth/start` flips the
   * session halfway through the sheet's life, and a sheet mounted under a
   * `!isAuthed` branch would unmount at exactly that moment: the buyer would
   * watch the chooser vanish between typing their address and picking a
   * gateway. Closed (`onceOpen === false`) it renders nothing, so mounting it
   * unconditionally costs a Radix root and no DOM — and for a signed-in buyer
   * the markup is character-for-character what it was inline.
   */
  /* ONE-TIME gateway chooser — bottom sheet on phones, centred on
      desktop. Hosted redirect(s) only, no card typed on our site.
      The +10% amount is repeated here as the FIRST line under the
      title: the gateway charges 439 000, and the buyer must have
      read that number on this screen before choosing an app. */
  const onceSheet = (
    <Dialog open={onceOpen} onOpenChange={(open) => (open ? setOnceOpen(true) : closeOnceSheet())}>
      {/* `p-6` wins over the sheet's own `pb-[env(safe-area-inset-bottom)]`
          in twMerge, so the bottom padding is restated here with the
          safe-area floor — otherwise the note sits under the iPhone
          home bar. `mx-auto` centres the sheet on 448–767px viewports
          (landscape phones), where `max-w-md` would pin it left. */}
      <DialogContent
        ref={onceSheetRef}
        /*
         * `overflow-y-auto` + the two sheet variables (2026-09-22). This is a
         * Radix bottom sheet capped at 92dvh with NO scroller of its own, so
         * when the keyboard came up for the guest's e-mail the lower half —
         * the gateway tiles and the pay control — was simply unreachable:
         * the body is scroll-locked, the sheet did not scroll, and on Android
         * Chrome the shrunken viewport just cut it off (tester, 2026-09-22:
         * «клавиатура перекрывает оставшиеся поля, скролл не работает»).
         * Now it scrolls, and `useVisualViewportSheetHeight` lifts and caps it
         * exactly like the card sheet — real keyboard or assumed.
         *
         * ⚠️ The no-var fallback is `92vh` with a `supports-[height:1dvh]`
         * twin (2026-09-26) — see the invoice sheet below for the measurement.
         * A lone `dvh` fallback is `max-height: none` on Chrome < 108.
         *
         * `gap-0` (2026-10-16): from `md` up `DialogContent` is the CENTRED
         * variant, a `grid gap-4`, so every child's own `mt-*` got 16px more
         * on desktop only — price → pill 28, pill → terms 32, terms → «Выберите»
         * 32 (owner, desktop: «чуть некрасиво смотрится … отступы»). The phone
         * sheet is a gapless flex column, so the margins below ARE the rhythm
         * there; with the gap gone desktop gets the same numbers, which are the
         * subscribe sheets' numbers too.
         */
        className="mx-auto mb-[var(--sheet-lift,0px)] max-h-[var(--sheet-max,92vh)] supports-[height:1dvh]:max-h-[var(--sheet-max,92dvh)] max-w-md gap-0 overflow-y-auto overscroll-contain !bg-bone-surface p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] md:mb-0"
        // With the amount line present Radix links it as the description;
        // without one (API one deploy behind) silence its warning.
        {...(oneTimeLabel ? {} : { 'aria-describedby': undefined })}
        data-testid="once-sheet"
      >
        {/* Explicit close — Esc / overlay / drag are invisible affordances,
            and a chooser with no way out reads as a trap on a phone. */}
        <DialogClose
          aria-label={t('plans.onceSheetClose')}
          className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-md text-[15px] text-ink transition-colors before:absolute before:-inset-2 before:content-[''] hover:bg-bone hover:text-ink"
        >
          {/* The same ✕ glyph as the Payme and Octobank sheets — three closes,
             one look (Igor, iPhone, 2026-09-24: «крестики на модалках разные»). */}
          ✕
        </DialogClose>
        {/* Same header as the subscribe sheet (card-save-modal): plan
            name at 16px, the price at 28px underneath. One shape for
            both sheets (Igor, 2026-09-09). */}
        <DialogTitle className="pr-10 text-[16px] font-semibold text-ink">
          {t('plans.onceSheetTitle', { name: planName })}
        </DialogTitle>
        {oneTimeLabel && (
          <DialogDescription className="mt-1 text-[28px] font-extrabold leading-none tracking-[-0.4px] text-ink">
            {oneTimeLabel}
          </DialogDescription>
        )}
        {/* Payback nudge (Igor, 2026-09-07): how many lots the payment
            buys access to — a signed-in buyer sees their own saved-search
            count, a guest sees the whole open catalogue. Same `livePill`
            the card already shows, so the two never disagree; rendered
            only when it resolved.

            ⚠️ Markup deliberately IDENTICAL to the VALUE PILL in the two
            subscribe modals (`card-save-modal.tsx`, `octo-subscribe-modal.tsx`)
            — the brand-dim rounded pill with a filled dot. It shipped here
            as a bare `•` + brand text, which put the same sentence in two
            different skins on the two sheets a buyer compares side by side.
            Copy is untouched; only the frame changed. If the pill is
            restyled, restyle all three together.

            The one extra class, `w-fit` (2026-10-16): the subscribe sheets put
            the pill in plain block flow, where an `inline-flex` is as wide as
            its text. Here it is a grid/flex item and was STRETCHED to the full
            sheet width — a teal band whose inset text no longer lined up with
            the terms line's flush icon under it. Sized to its text, it reads
            as the same chip, left-aligned with the terms line, as there. */}
        {livePill && (
          <div className="mt-3 inline-flex w-fit items-center gap-2 rounded-full bg-brand-dim px-3 py-1.5 text-[12.5px] font-medium leading-snug text-brand">
            <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-brand" />
            <span>{livePill}</span>
          </div>
        )}
        {/* THE TERMS LINE — the one-time sheet's counterpart of the renewal
            line on the two subscribe sheets: same frame, same position under
            the price (`CheckoutTermsLine`, 2026-09-23). It replaces the dated
            «Оплата и доступ» box: how long access lasts, until when, and that
            nothing renews.
            Since 2026-10-11 (owner) it leads with the same refund headline as
            the two subscribe sheets — «7 дней — гарантия возврата средств» —
            so the three sheets a buyer compares say the same thing in the same
            place; the access sentence is the second line. The refund row in
            the trust block below is gone with it, so it is said once.
            No `className`: the component's own `mt-3`, the same 12px it gets
            under the pill on the subscribe sheets (2026-10-16; was `mt-4`). */}
        {oneTimeLabel && onceUntil && (
          <CheckoutTermsLine headline={t('billing.card.refund7')}>
            {/* `word` agrees with the count in Russian («31 день», «22 дня»,
                «30 дней») — a calendar month is 28–31 days, so a fixed «дней»
                was wrong in most months. uz/kk spell their invariant noun. */}
            {t('plans.onceAccessLine', {
              days: onceDays,
              word: daysWord(onceDays, locale),
              date: onceUntil,
            })}
          </CheckoutTermsLine>
        )}
        {/* GUEST: the address, directly above the payment method — the same
            position it takes on the card sheet, and the only difference between
            this sheet and the one a signed-in buyer sees. Null for a member. The
            account is created by the tap that starts the payment: `POST
            /billing/checkout` needs a `user_id` and there is no second sheet left
            to ask on.

            ⚠️ `active`, not just `node` — the SAME test the Octobank body uses.
            `node` is keyed on the sticky «this checkout began as a guest» latch,
            which never falls; `active` also asks whether this browser is still a
            guest. Once `commit()` has traded the checkout token for a real
            session — which happens BEFORE the hosted redirect, so that the
            provider returns to a browser that knows who it is — the question has
            an answer and the box has nothing left to ask. It stayed on screen in
            every case where the redirect does not end this document: the
            Instagram/Android hand-off leaves this page up, and a gateway the
            buyer comes back from re-opens this sheet (Igor, 2026-09-21).

            ⚠️ …on the NEXT open, not under an open sheet (`onceIdentifyPinned`,
            2026-10-02) — see the note on the pin. */}
        {(onceIdentify.active || onceIdentifyPinned) && onceIdentify.node && (
          <div className="mt-4">{onceIdentify.node}</div>
        )}
        {/* «ОПЛАТА ПО ТЕЛЕФОНУ» (the SMS-invoice body that replaced this one
            inside Instagram/Telegram on Android, 27.09–30.09) is gone: 0 of 11
            buyers who got the SMS paid, and that browser now sells the card
            subscription only (`cardOnly`), so it never opens this sheet unless
            recurring is down — when the hosted body below is the right one. */}
        <>
          <p className="mt-4 text-[13px] text-ink-muted">{t('plans.onceSheetPick')}</p>
          {/* THE GATEWAY STRIP IS NOW A CHOICE, NOT THE PAYMENT.
              Picking is what lets the order be created before the pay gesture, so
              the pay control below can be a real link — see `usePreparedCheckout`.
              The first gateway is selected by default, so nobody who ignores this
              strip is worse off than they were, and switching re-uses an order
              already made for the other one rather than minting a second. */}
          <div
            aria-label={t('plans.onceSheetPick')}
            className={`mt-2.5 grid gap-2.5 ${
              payOnceGateways.length > 1 ? 'grid-cols-2' : 'grid-cols-1'
            }`}
          >
            {payOnceGateways.map(({ m, logo, ariaKey }) => (
              <button
                key={m}
                type="button"
                /* A toggle button, NOT a radio: it keeps the accessible name and
                   the role the tile has always had, and `aria-pressed` is the
                   honest state for "this is the app your payment will open". */
                aria-pressed={preparingFor === m}
                onClick={() => setOnceGateway(m)}
                aria-label={t(ariaKey)}
                className={`inline-flex h-[60px] items-center justify-center rounded-lg border-[1.5px] px-3 transition-colors ${
                  preparingFor === m
                    ? 'border-brand bg-brand-dim'
                    : 'border-edge bg-bone-surface hover:border-brand hover:bg-brand-dim'
                }`}
              >
                <img src={logo} alt="" className="h-[22px] w-auto" />
              </button>
            ))}
          </div>
          <div className="mt-2.5">{payOnceCta}</div>
          {/* Where the payment page will open — Instagram/Android only. */}
          {externalHint}
          {/*
            THE HAND-OFF DID NOT HAPPEN. Shown only after `watchHandoff` waited
            2.5 s and this page is still on screen, i.e. the WebView swallowed the
            intent. Three routes out, ordered by how likely each is to work inside
            an in-app browser: copy, open in a new tab, read it off the screen.
            The raw URL is `select-all` because clipboard access is
            permission-gated in these browsers — the lesson `invoice-flow.tsx`
            already learned, with the same `catch`.
          */}
          {handoffStuck && (
            <div
              data-testid="handoff-fallback"
              className="mt-4 rounded-lg border border-edge bg-bone p-3.5"
            >
              <p className="text-[13px] font-semibold leading-snug text-ink">
                {t('plans.handoffStuck')}
              </p>
              <button
                type="button"
                onClick={() => {
                  /* `copyLink` also covers the browsers that expose no
                     `navigator.clipboard` at all — there `?.writeText()` yields
                     `undefined`, and a `.catch()` on it throws inside the very
                     in-app WebView this panel exists for. */
                  void copyLink(handoffStuck)
                    .then((ok) => {
                      setHandoffCopied(ok);
                      if (ok) window.setTimeout(() => setHandoffCopied(false), 2000);
                    })
                    .catch(() => setHandoffCopied(false));
                }}
                className="mt-2.5 inline-flex h-9 items-center rounded-md border-[1.5px] border-brand px-3 text-[13px] font-bold text-brand transition-colors hover:bg-brand-dim"
              >
                {handoffCopied ? t('plans.handoffCopied') : t('plans.handoffCopy')}
              </button>
              <a
                href={handoffStuck}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 block select-all break-all text-[12px] leading-snug text-brand underline"
              >
                {handoffStuck}
              </a>
            </div>
          )}
          {checkout.isPending && (
            <p className="mt-3 text-center text-[12px] leading-snug text-ink-muted">
              {t('plans.redirecting')}
            </p>
          )}
          {checkout.isError && (
            <p className="mt-2 text-center text-[13px] text-status-closing-text" role="alert">
              {checkout.error instanceof ApiError && checkout.error.code === 'tin_required'
                ? t('plans.tinRequired')
                : t('plans.checkoutError')}
            </p>
          )}
          {/* ONE line, and only when the background preparation failed and the
              press has not yet produced its own error — the button above is
              already back to create-then-navigate, so this says what happened
              without claiming the purchase is over. */}
          {prepared.state.status === 'failed' && !checkout.isError && (
            <p
              data-testid="once-prepare-error"
              className="mt-2 text-center text-[13px] text-status-closing-text"
              role="alert"
            >
              {t('plans.checkoutError')}
            </p>
          )}
        </>
        {/* TRUST BLOCK — processor and offer/privacy/support, as under the
            subscribe card (card-save-modal); the first-payment refund moved
            up into the terms line's headline on 2026-10-11. The processor line names whichever
            gateways this sheet actually offers; «обрабатывает Payme»
            under a Click button would be a lie. Measured 2026-09-07,
            day 1.5 after the redesign: 9 people opened this sheet 39
            times and 2 paid — a sheet with only two logos reads as a
            phishing popup, not a checkout. */}
        <div className="mt-5 flex flex-col items-center gap-1.5 border-t border-edge-light pt-4 text-center text-[12px] leading-snug text-ink-soft">
          <span className="inline-flex items-center justify-center gap-1.5">
            <svg
              viewBox="0 0 24 24"
              className="h-4 w-4 shrink-0 text-brand"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <rect x="5" y="11" width="14" height="9" rx="2" />
              <path d="M8 11V8a4 4 0 0 1 8 0v3" />
            </svg>
            {t(
              payOnceGateways.length > 1
                ? 'billing.card.securedByPayClick'
                : payOnceGateways[0]?.m === 'click'
                  ? 'billing.card.securedByClick'
                  : 'billing.card.securedByPay',
            )}
          </span>
          <span>
            <Link
              to="/offer"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-brand hover:underline"
            >
              {t('billing.card.offerLink')}
            </Link>
            {' · '}
            <Link
              to="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-brand hover:underline"
            >
              {t('billing.card.privacyLink')}
            </Link>
            {HAS_TELEGRAM && (
              <>
                {' · '}
                <a
                  href={COMPANY.telegramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-brand hover:underline"
                >
                  {t('billing.card.supportTg')}
                </a>
              </>
            )}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );

  /*
   * «Оплата по расчётному счёту», hoisted out of the authed branch for the same
   * reason: a yearly GUEST now identifies INSIDE it. Only the trigger button
   * stayed behind in the yearly branch; the dialog body is identical.
   */
  /* Requisites open in a DIALOG, not inline: an inline expand made
      this card tower over its sibling and wrecked the grid alignment.
      The modal keeps the card grid stable and gives the long requisites
      room (bottom sheet on mobile, centered dialog on desktop). */
  const bankSheet = (
    <Dialog open={showBank} onOpenChange={setShowBank}>
      {/* A FLEX COLUMN that does not scroll, `p-0`: `InvoiceFlow` owns the two
          halves inside it — a scrolling body and the pinned «Выставить счёт»
          footer, the same shape the card sheet has had since 8435555f. The
          dialog used to be one `overflow-y-auto p-6` box, so on a 375×667 phone
          the button sat below eleven rows of bank requisites and the buyer had
          to find it. */}
      <DialogContent
        /*
         * THE TWO SHEET VARIABLES, so the keyboard cannot bury the button
         * (2026-09-25). `InvoiceFlow` now calls `useVisualViewportSheetHeight`
         * like every other payment sheet; this is the panel that CONSUMES what
         * it publishes, because the lift has to land on the `position: fixed`
         * box and `--sheet-max` has to beat the `88vh` this line used to state
         * outright. On iOS the keyboard shrinks the VISUAL viewport only, so
         * `88vh` stayed 88% of the full screen and the pinned «Выставить счёт»
         * footer sat behind the keys, on a panel that fitted inside its own
         * max-height and therefore had nothing to scroll. Both properties are
         * REMOVED when no keyboard is up, so the fallbacks — `88dvh` and `0px`
         * — are the shape this sheet has always had.
         *
         * ⚠️ BOTH SPELLINGS, and in this order (2026-09-26, second pass). The
         * fallback is not decoration: `InvoiceFlow` deliberately gives both
         * properties back whenever no keyboard can be measured, and on a build
         * that resizes the LAYOUT viewport for the keyboard (Chrome on Android
         * with `interactive-widget=resizes-content`) that is exactly when the
         * keys are up — so the fallback wants to be a unit the keyboard shrinks,
         * which `dvh` is and a static `vh` (the LARGE viewport) is not.
         *
         * But a `dvh` ALONE is worse than a stale cap: `dvh` shipped in Chrome
         * 108, and on Chrome 101 (Android 9, Xiaomi Mi 8 Lite —
         * `CSS.supports('height','1dvh')` false, measured on Igor's device
         * 2026-09-26) `max-height: var(--sheet-max, 88dvh)` is invalid at
         * computed-value time and computes to **`none`**. The sheet was then
         * uncapped in every state on that phone — 483px of panel — while
         * `--sheet-lift` (px) applied as written and put its header at −237.
         *
         * So: `88vh` plain for the browsers without `dvh` (a real cap, if a
         * generous one — the px cap the two hooks write is what actually holds
         * the keyboard cases there), and the identical `88dvh` inside
         * `@supports (height: 1dvh)` for everyone else, which Tailwind emits
         * AFTER the plain rule at equal specificity, so behaviour on a modern
         * browser is byte-for-byte what it was. 88 (not 92) is the height this
         * sheet has always had without a keyboard.
         */
        className="mx-auto mb-[var(--sheet-lift,0px)] flex max-h-[var(--sheet-max,88vh)] supports-[height:1dvh]:max-h-[var(--sheet-max,88dvh)] max-w-md flex-col overflow-hidden !bg-bone-surface p-0 md:mb-0"
        /*
         * OPEN QUIETLY — no keyboard (Igor, 2026-09-20).
         *
         * Nothing in `InvoiceFlow` asks for focus; this is Radix, which focuses
         * the first tabbable descendant of a `DialogContent` on mount. That
         * descendant is the ИНН input (the dialog has no close button of its
         * own), so the phone keyboard slid up the instant the sheet appeared
         * and covered the plan name, the amount and the access line — the three
         * things the sheet was just given a header to say. The card sheet has
         * refused to auto-focus its card step since 2026-09-09 for exactly this
         * reason; this makes the счёт agree with it.
         *
         * `preventDefault` leaves focus on the content element itself, so Esc
         * and the Tab trap keep working. Focus-on-ERROR is untouched: a press
         * that cannot go through still puts the caret in the first bad field.
         */
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* The BODY changed, the dialog did not. `InvoiceFlow` now
            ISSUES a numbered счёт with its own payment purpose instead
            of showing static requisites and a /schet link — see its
            docstring. The trigger, the tracking and the card grid are
            untouched on purpose. */}
        {/*
         * ONE form for a guest, the same form for everyone else.
         *
         * Igor, 2026-09-19: «можно без "далее" — просто поле почты появляется и
         * всё, ИНН вводит, если хочет название, дальше скачать и отправляет по
         * почте либо скачивает сразу». So the address is not a step in front of
         * `InvoiceFlow`, it is a FIELD inside it: `identify` puts the email above
         * the ИНН, `beforeSubmit` makes the one «Выставить счёт» press create the
         * account before it creates the счёт.
         *
         * Both are undefined for a signed-in buyer, so this is byte-for-byte the
         * call it has always been. `beforeSubmit` is withdrawn again the moment
         * identification succeeds — a second press (a failed issue, a retry) has
         * nothing left to do.
         */}
        <InvoiceFlow
          amountLabel={invoiceAmountLabel}
          planKey={plan.key}
          /* WHICH plan, in the card sheet's own title pattern («Pro на год» /
             «Бизнес на год»). It is the Radix dialog title as well, so the
             sheet's accessible name names the plan instead of the payment
             method. */
          heading={
            <DialogTitle className="text-[16px] font-semibold text-ink">
              {`${planName} ${t('billing.card.periodAnnual')}`.trim()}
            </DialogTitle>
          }
          identify={bankIdentify.active ? bankIdentify : undefined}
          beforeSubmit={bankIdentify.active ? bankIdentify.ensure : undefined}
          /* …and the other end: the счёт is issued, so the guest becomes a
             member and can find it in their account. Same guard, same reason —
             undefined for a signed-in buyer, so their invoice is byte-for-byte
             the call it has always been. */
          afterIssued={bankIdentify.active ? bankIdentify.commit : undefined}
        />
      </DialogContent>
    </Dialog>
  );

  // The "×2" claim, rounded for display — null when the prices don't back it up.
  const x2 = dealMultiple(deal);

  // Payment methods per plan (all visible, no disclosure):
  //   • Monthly → TWO filled CTAs: card-on-file subscribe (opens the card modal)
  //               and «Оплатить разово» (opens the Payme/Click chooser sheet).
  //   • Yearly  → расчётный счёт (bank transfer) + one-time Payme/Click; no subscribe.
  //   • recurringEnabled === false → monthly falls back to the one-time buttons inline.

  return (
    <div
      data-testid="plan-card"
      className={`relative flex h-full flex-col rounded-xl border bg-bone-surface p-5 transition-shadow sm:p-6 ${
        recommended
          ? 'border-brand shadow-m-md ring-1 ring-brand/15'
          : 'border-edge shadow-m-sm hover:shadow-m-md'
      }`}
    >
      {/* Ribbon — «Популярный» on the featured (Pro) plan. The «×2 выгода» headline
          lives on the period toggle now, so keep the ribbon simple (no duplication). */}
      {recommended && (
        <span className="absolute -top-2.5 right-5 inline-flex items-center rounded-pill bg-brand px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.04em] text-white shadow-m-sm">
          {t('plans.badgePopular')}
        </span>
      )}

      {/* #15: both paid cards are named "Pro" in the DB, so the tier alone
          cannot be told apart. Append the billing period to the TITLE (the price
          suffix below is easy to miss). */}
      <div className="mb-1 text-[15px] font-bold text-ink">
        {`${planName} — ${plan.billingPeriod === 'monthly' ? t('plans.periodMonth') : t('plans.periodYear')}`}
      </div>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        {/* The subscription price — the one-time figure sits on its own button. */}
        <span className="font-brand text-[27px] font-extrabold leading-none tracking-[-0.3px] text-ink">
          {formatBudget(String(plan.priceUzs))}
        </span>
        <span className="text-[13px] font-medium text-ink-muted">
          {t('plans.som')}{' '}
          {plan.billingPeriod === 'monthly' ? t('plans.perMonth') : t('plans.perYear')}
        </span>
        {/* Anchor: what that same year costs month-to-month. Carries an
            aria-label because a bare struck-through number reads as noise. */}
        {x2 && deal && (
          <span
            className="text-[13px] font-medium text-ink-muted line-through"
            aria-label={t('plans.refYearAria', {
              som: formatBudget(String(deal.refYearUzs)) ?? '',
            })}
          >
            {formatBudget(String(deal.refYearUzs))}
          </span>
        )}
      </div>
      {/* Savings vs paying monthly — reserve the line's height on every card
          (invisible when absent) so prices and feature lists start on the same row. */}
      <div className="mt-1.5 min-h-[16px] text-[12px] font-semibold leading-[1.4] text-brand">
        {x2 && deal
          ? t('plans.savingsX2', { som: formatBudget(String(deal.savingsUzs)) ?? '' })
          : deal
            ? t('plans.savings', { percent: deal.percent })
            : ' '}
      </div>

      {/* LIVE-COUNT PILL — «По вашим поискам сейчас 21 открытый лот» for a
          signed-in buyer, «Сейчас 3 512 открытых тендеров» for a guest. The slot
          keeps its height while the count loads so the feature list below does
          not jump when it arrives. */}
      <div className="mb-4 mt-3 min-h-[36px]">
        {livePill && (
          <div
            data-testid="live-pill"
            className="flex items-center gap-2 rounded-lg bg-brand-dim px-3 py-2 text-[13px] font-semibold leading-snug text-brand"
          >
            <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-brand" />
            <span>{livePill}</span>
          </div>
        )}
      </div>

      <div className="mb-6 flex flex-1 flex-col gap-4 border-t border-edge pt-4">
        {featureGroups.map((g, gi) => (
          <div key={gi}>
            {g.title && (
              <div
                /* Both variants carry the SAME brand accent as the tick glyphs
                   below them. The `plus` line («Всё из Pro, плюс:») shipped grey
                   (`text-ink-soft`) from the day it was introduced in 9cb1d2bd,
                   which read as a disclaimer sitting above the list rather than
                   as the thing the Business card is selling; it is the only
                   group header on the page that was not brand-coloured. Sentence
                   case and 13px are kept because it is a sentence, not the short
                   uppercase group label the Pro card uses. */
                className={
                  g.plus
                    ? 'text-[13px] font-bold text-brand'
                    : 'text-[11px] font-bold uppercase tracking-[0.08em] text-brand'
                }
              >
                {g.title}
              </div>
            )}
            <ul className={`flex flex-col gap-2 ${g.title ? 'mt-2' : ''}`}>
              {g.items.map((label, i) => (
                <li key={i} className="flex items-start gap-2 text-[13px] text-ink-soft">
                  <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* ── Checkout (Layout B) — one primary CTA + a methods disclosure ─────── */}
      {STRIPE_CHECKOUT && !isCurrentPlan ? (
        /* Kazakhstan: one button → Stripe Checkout, for a guest and a member
           alike (a guest signs in first). Nothing below this branch renders. */
        <div data-testid="stripe-checkout">
          <button
            type="button"
            disabled={!ctaReady || stripeCheckout.isPending || stripeCheckout.isSuccess}
            aria-busy={stripeCheckout.isPending || undefined}
            onClick={startStripeCheckout}
            className={`${primaryCtaClass} disabled:cursor-wait disabled:opacity-70`}
          >
            <span className="inline-flex min-w-0 items-center gap-1.5">
              {stripeCheckout.isPending ? <Spinner size="sm" /> : <LockIcon className="shrink-0" />}
              <span>{t('plans.subscribeWithCard', { name: planName })}</span>
            </span>
            <span className="shrink-0 whitespace-nowrap text-[14px] md:text-[13.5px]">
              {som(Number(plan.priceUzs))}
            </span>
          </button>
          <p className="mt-2 text-center text-[12px] leading-snug text-ink-muted">
            {t('plans.stripe.renews')}
          </p>
          {stripeError && (
            <p
              role="alert"
              data-testid="stripe-checkout-error"
              className="mt-2 text-center text-[12.5px] leading-snug text-status-closing-text"
            >
              {stripeError}
            </p>
          )}
          {paymentFootnote}
        </div>
      ) : isCurrentPlan ? (
        /* Current plan — a highlighted badge, no CTA (they're already on it). */
        <div className="inline-flex h-[42px] w-full items-center justify-center gap-1.5 rounded-md border-[1.5px] border-brand bg-brand-dim text-[14px] font-bold text-brand">
          <CheckIcon className="h-4 w-4" />
          {t('plans.currentPlan')}
        </div>
      ) : !ctaReady ? (
        /* Not yet knowable. Same height and shape as the real CTA so the card
           doesn't shift when it resolves, but disabled — the whole defect was
           offering a button whose action had not been decided yet. */
        <button
          type="button"
          disabled
          aria-busy="true"
          /* Same box as the real CTA it becomes — `min-h-[52px] md:min-h-[46px]`,
             matching `primaryCtaClass` — so resolving the query does not jog the
             card. It kept `h-[42px]` when the CTAs grew, which is the shift this
             placeholder exists to prevent. */
          className="inline-flex min-h-[52px] w-full cursor-wait items-center justify-center rounded-md bg-brand px-4 py-2 text-[14px] font-bold text-white opacity-60 md:min-h-[46px]"
        >
          {t('plans.checkout')}
        </button>
      ) : !isAuthed ? (
        /* Guest. The CTA is unchanged in label and position; what it OPENS is
           the change — one of the same three sheets a signed-in buyer gets, with
           one extra field in it (Igor, 2026-09-19: «у меня, как у гостя, точно
           такое же окно, как у зарегистрированного… просто в этих модалках есть
           дополнительное поле, в котором я указываю email. И всё»). */
        <>
          {/* The SAME column the member's two CTAs live in — `flex flex-col
              gap-2`, not a `mt-2` on the second button (Igor, 2026-09-20).
              Spacing is part of "the same buttons". */}
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => {
                /*
                 * The login wall, recorded.
                 *
                 * This click produced NO event at all, so a guest who wanted to buy
                 * and a guest who wandered off looked identical — and the whole
                 * 1,330 `plans_viewed` → 73 `checkout_started` drop read as "checkout
                 * is broken" when an unknown share of it was simply "not signed in".
                 *
                 * ⚠️ The event STAYS, and fires at the same moment it always did —
                 * the click that reveals the identification step. 245 guests reached
                 * it in the 30 days to 2026-09-18 and it is their denominator; if it
                 * vanished along with the /login redirect the metric would zero and
                 * read as "fixed".
                 */
                track('signup_wall_shown', { plan: plan.key, source: 'plansCheckout' });
                /*
                 * Remember the plan anyway. The sheet below keeps the buyer here,
                 * but a guest who closes it, or who falls back to /login from the
                 * password step, must still come back to this plan — `postAuthPath()`
                 * reads this. `intent: 'pay'` because this IS the pay press: it is
                 * the click that opens the guest checkout sheet.
                 */
                stashPendingCheckout({ planKey: plan.key, intent: 'pay' });
                if (guestTarget === 'card') {
                  /*
                   * The card sheet IS the checkout now, so the ladder's denominator
                   * is fired here — the same moment, and with the same meta, as the
                   * authed «Подписаться» click. `via:'guest'` is the only difference
                   * (a meta key, not a second event name: every rung below it is the
                   * same event and would otherwise have its denominator split).
                   */
                  setSubscribePlan(plan);
                  setPayMethod('payme'); // Payme by default; Octobank is the option
                  setMethodSwap(false); // a fresh checkout — the sheet animates in
                  /*
                   * ⚠️ `deferMeta` — the first-party ladder starts HERE, exactly
                   * as before, but Meta's InitiateCheckout waits for the first
                   * digit of the card number (`onPaymentEntryStarted` below) or
                   * for «Продолжить на Octobank». Opening this sheet is not
                   * payment intent Meta should optimise on: 1 guest in 31 who
                   * opened one on 19–21.09 paid, against 11 in 86 members.
                   */
                  setSubscribeMetaEventId(
                    fireBeginCheckout('card', { via: 'guest', deferMeta: true }),
                  );
                  setGuestSheetOpen(true);
                } else if (guestTarget === 'invoice') {
                  /*
                   * ANNUAL: straight into the invoice sheet. `invoice_requested` and
                   * the Meta/GA4 invoice begin-checkout fire HERE, on the click that
                   * reveals the flow — the same moment and meaning as the authed
                   * «Оплата по расчётному счёту» toggle, so the series stays one
                   * series. `source` is the only thing that differs
                   * ('plansCheckout', the control actually clicked, matching the
                   * `signup_wall_shown` above), which is what lets a guest счёт be
                   * told apart from a signed-in one without a new event name.
                   *
                   * ⚠️ It fires BEFORE identification, i.e. before the ИНН form is on
                   * screen. Deliberate, and it keeps the event's meaning: it has
                   * always marked "this buyer chose bank transfer", never "this
                   * buyer reached the ИНН field". `invoice_issued` remains the only
                   * event that means a document exists.
                   */
                  fireBeginCheckout('invoice');
                  track('invoice_requested', { plan: plan.key, source: 'plansCheckout' });
                  setShowBank(true);
                } else {
                  // Recurring is off (a Payme outage): the hosted chooser, which is
                  // exactly where the authed monthly card degrades to as well.
                  openOnceSheet();
                }
              }}
              className={guestCta.className}
            >
              {guestCta.face}
            </button>
            {/* The card-shy buyer's path, opened to guests for the first time.
              «Напрягает, что номер карты запрашивает» is the objection the hosted
              one-time answers, and until now the whole block rendered only for a
              signed-in user — so the guests it is written for could not see it.
              Same button, same position and same price as on the authed monthly
              card. Withheld on an ИНН-gated plan: the advisory gate inside reads
              `user.tin`, which a guest does not have, and would open a company
              modal at someone with no account. Withheld inside Instagram /
              Telegram on Android too (`cardOnly`), exactly as for a member. */}
            {guestTarget === 'card' && !cardOnly && oneTimeLabel && !plan.requiresTin && (
              /* ⚠️ `primaryCtaClass` — the MEMBER'S button, filled, not an
               outline (Igor, 2026-09-20: «„Оплатить разово" такое же, как у
               зарегистрированного, а не блеклое»). The member's one-time CTA is
               not a ghost or a text link: it is the same solid brand button as
               «Подписаться на Pro» directly above it, deliberately, because
               measured per button the two-filled-button layout sold 12.3
               payments per 100 plans viewers against 7.8 without the one-time.
               A guest offered a washed-out version of the same choice is being
               told, in the only language a button has, that it is the lesser
               one. Byte-identical to the member's: same class constant, same
               face, same column. */
              <button type="button" onClick={openOnceSheet} className={primaryCtaClass}>
                <span className="inline-flex min-w-0 items-center gap-1.5">
                  <CardGlyph />
                  <span>{t('plans.payOnceBtn')}</span>
                </span>
                <span className="shrink-0 whitespace-nowrap text-[14px] md:text-[13.5px]">
                  {oneTimeLabel}
                </span>
              </button>
            )}
          </div>
          {guestTarget === 'card' && cardOnly && cardOnlyNote}
          {paymentFootnote}
        </>
      ) : null}
      {/* The guest's sheets are mounted OUTSIDE the `isAuthed` gates below.
          `/auth/start` flips the session while the buyer is still typing;
          anything hung off `!isAuthed` would unmount at that exact moment and
          discard the card. All of them render nothing while closed. */}
      {!STRIPE_CHECKOUT && guestSheetOpen && (
        <>
          <CardSaveModal
            reminderChannel="email"
            open={payMethod === 'payme'}
            mode="subscribe"
            planKey={subscribePlan.key}
            planName={planDisplayName(subscribePlan, t)}
            priceLabel={som(subscribePlan.priceUzs)}
            billingPeriod={subscribePlan.billingPeriod}
            metaEventId={subscribeMetaEventId}
            methodTabs={methodTabs}
            identify={cardIdentify}
            /* QUALIFIED ACTION #1 — the first digit of the card number is what
               sends Meta this guest's InitiateCheckout, held back when the sheet
               opened. The IC half is GUEST ONLY: the member's sheet below fires
               AddPaymentInfo from here too, but its IC fired on «Подписаться»
               and stays there. */
            onPaymentEntryStarted={() => {
              fireDeferredMetaIc(subscribeMetaEventId, 'card');
              // BOTH, under one id: the guest's held-back IC and the qualified
              // action itself. Different names, so Meta counts two events.
              fireAddPaymentInfo(subscribeMetaEventId, 'card');
            }}
            /* A gateway swap closes and re-opens this sheet. Without this the
               reset-on-open would wipe a correctly typed card because the buyer
               looked at Octobank — while the address beside it survived, since
               it lives here in the plan card. Guest-only: the sheet is unmounted
               outright on close, so a re-open can ONLY be a swap. */
            preserveOnReopen
            /* …and, for the same swap, no entrance. See `methodSwap`: only this
               sheet has one, so replaying it on a radio press made one
               direction of the toggle look like a different modal opening. */
            enterAnimation={!methodSwap}
            // No `nicheHint`: it comes from `/billing/subscribe-hint`, which is
            // authed-only and is about saved searches a guest cannot have yet.
            // The pill is simply absent, exactly as it is for a member whose
            // hint has not resolved.
            onSwitchToOcto={octoGateway ? () => switchMethod('octo') : undefined}
            onClose={closeGuestSheet}
            onSaved={() => {
              // Same ending as the authed card flow: /billing/return fires the
              // BROWSER leg of the Meta purchase event, the one moment after
              // payment when `_fbc`/`_fbp` exist. See the authed `onSaved`.
              void refresh().finally(() => {
                setGuestSheetOpen(false);
                void navigate('/billing/return?src=card');
              });
            }}
          />
          {/* Octobank, reached from the strip on the body above. It gets the
              SAME `cardIdentify` object — one instance for both bodies of this
              card — so the address, the revealed code field and the error follow
              the buyer across the switch, and «Продолжить на Octobank»
              identifies before it redirects. Once identified the prompt
              disappears from both at once. */}
          {octoGateway && (
            <OctoSubscribeModal
              reminderChannel="email"
              open={payMethod === 'octo'}
              planKey={subscribePlan.key}
              planName={planDisplayName(subscribePlan, t)}
              priceLabel={som(subscribePlan.priceUzs)}
              billingPeriod={subscribePlan.billingPeriod}
              metaEventId={subscribeMetaEventId}
              methodTabs={methodTabs}
              identify={cardIdentify}
              /* Tells the server this hosted order belongs to a guest — the same
                 flag the one-time sheet sends on `POST /billing/checkout`. */
              via="guest"
              /* QUALIFIED ACTION #3 — «Продолжить на Octobank» is the gesture
                 that leaves for the provider. The id is SHARED with the Payme
                 body, so a buyer who typed a card digit first sends one IC. */
              onProviderHandoff={() => {
                fireDeferredMetaIc(subscribeMetaEventId, 'card');
                fireAddPaymentInfo(subscribeMetaEventId, 'card');
              }}
              onClose={closeGuestSheet}
            />
          )}
        </>
      )}
      {/* ⚠️ `onceOpen ||` — an OPEN sheet stays mounted when this plan becomes the
          current one underneath it. «Оплата по телефону» is the one path where
          that happens with the sheet still on screen: the payment lands, the
          sheet refreshes the session to show «Оплачено», and without this the
          refreshed session would unmount the very sheet saying so. */}
      {!STRIPE_CHECKOUT && (onceOpen || (!isCurrentPlan && ctaReady)) && onceSheet}
      {/* The invoice sheet, on the annual card only (nothing else opens it) and
          outside the `isAuthed` gate for the same reason as the two above. */}
      {!STRIPE_CHECKOUT && !isCurrentPlan && ctaReady && isYearly && bankSheet}
      {STRIPE_CHECKOUT || isCurrentPlan || !ctaReady || !isAuthed ? null : (
        <div>
          {/*
           * ⚠️ MONTHLY = subscription PRIMARY + a quiet one-time link under it.
           *
           * History, because the arrangement is load-bearing. The one-time was
           * removed 2026-08-22 on the reading that it only moved buyers off
           * recurring (10–16.08 and 16–22.08 both sold 16 pro plans, with and
           * without the button). Ten days without it disproved that: pro payments
           * fell to 5–9/week against the 16/week baseline while SUBSCRIPTIONS
           * stayed at ~7/week — the button had not been stealing subscribers, and
           * removing it removed only the one-time buyers. Substitution measured
           * over the whole period: 0 of ~11 weekly one-time buyers took a
           * subscription instead. That tripped the rollback criterion recorded
           * here (below ~10/week for two consecutive weeks), so it is restored.
           *
           * The card objection it answers is real — a prospect said «напрягает,
           * что номер карты запрашивает… меня это и остановило» — which is why
           * the one-time keeps the HOSTED Payme path (no card typed on our site).
           *
           * NEW ROLLBACK CRITERION (the other direction): if subscriptions fall
           * below ~5/week for two consecutive weeks while one-time sales run, the
           * hierarchy is leaking and the link goes back behind a failure state.
           *
           * Yearly plans have NO subscribe path (the server refuses `subscribe-card`
           * for annual), so there the one-time charge is the only CTA and stays
           * primary — as it also does for monthly when `recurringEnabled` is false:
           * a Payme recurring outage must degrade checkout, not brick it. Both live
           * in the `else` branch.
           */}
          {recurringEnabled && !isYearly ? (
            /* MONTHLY: two filled CTAs, subscribe first (2026-09-06).

               The tab picker this replaces (05–06.09) hid the one-time behind a
               switch; before it, the one-time was removed outright (22.08) and
               restored after pro sales fell 16 → 5–9/week while SUBSCRIPTIONS
               held ~7/week either way. Measured per button over the whole
               period: the two-button layout sold 12.3 payments per 100 plans
               viewers against 7.8 without the one-time; the subscribe click
               rate was flat (20–24%) across every regime. So both buttons are
               shown, both filled, subscribe on top — the one-time is additive,
               not a leak from recurring.

               Each button carries ITS OWN price on the right, so the +10%
               one-time figure is on screen before anyone taps it. No
               «Автопродление · отмена в любой момент» line under the subscribe
               button any more: the renewal disclosure lives in the card modal's
               dated timeline, where the buyer decides. */
            <>
              <div className="flex flex-col gap-2">
                {cardOnly ? (
                  /* Instagram / Telegram on Android: the card sheet is the only
                     way to pay that works there (see `cardOnly`), so it is the
                     only button — worded as a payment, with the renewal said
                     plainly underneath (`cardOnlyNote`). */
                  <button
                    type="button"
                    data-testid="card-only-cta"
                    onClick={openSubscribeModal}
                    className={primaryCtaClass}
                  >
                    {cardOnlyFace}
                  </button>
                ) : (
                  <>
                    <button type="button" onClick={openSubscribeModal} className={primaryCtaClass}>
                      <span className="inline-flex min-w-0 items-center gap-1.5">
                        <LockIcon className="shrink-0" />
                        <span>{t('plans.subscribeWithCard', { name: planName })}</span>
                      </span>
                      <span className="shrink-0 whitespace-nowrap text-[14px] md:text-[13.5px]">
                        {som(Number(plan.priceUzs))}
                      </span>
                    </button>
                    <button type="button" onClick={openOnceSheet} className={primaryCtaClass}>
                      <span className="inline-flex min-w-0 items-center gap-1.5">
                        <CardGlyph />
                        <span>{t('plans.payOnceBtn')}</span>
                      </span>
                      {oneTimeLabel && (
                        <span className="shrink-0 whitespace-nowrap text-[14px] md:text-[13.5px]">
                          {oneTimeLabel}
                        </span>
                      )}
                    </button>
                  </>
                )}
              </div>
              {cardOnly && cardOnlyNote}
              {paymentFootnote}
            </>
          ) : isYearly && oneTimeLabel ? (
            /* YEARLY: расчётный счёт is the filled primary (settled by hand, priced
               as one payment), with card-via-app (Payme/Click) offered below. */
            <>
              <button
                type="button"
                onClick={() => {
                  // Choosing «по счёту» is payment intent too — count it, but
                  // ONLY on open. This is a toggle, so firing unconditionally
                  // sent a Meta InitiateCheckout on every collapse as well:
                  // one person idly expanding and re-collapsing produced three
                  // "checkouts". Those events are browser-only (fireBeginCheckout
                  // never reaches our API, and /meta/events allowlists just
                  // ViewContent + Search, so no CAPI twin is even possible), so
                  // each one landed in Meta with no email and no server pair —
                  // dragging the InitiateCheckout match-quality percentages down
                  // and teaching the optimiser on intent nobody expressed.
                  if (!showBank) {
                    fireBeginCheckout('invoice');
                    // Recorded as its own event, NOT `checkout_started`: this branch
                    // reaches no API endpoint (it only reveals requisites) and an
                    // invoice is settled + activated by hand, never producing `paid`.
                    track('invoice_requested', { plan: plan.key, source: 'plansInvoice' });
                  }
                  setShowBank((v) => !v);
                }}
                aria-expanded={showBank}
                className={invoiceCtaClass}
              >
                <BankGlyph />
                {t('plans.payByAccount')}
                <ChevronDownGlyph className={showBank ? 'rotate-180' : ''} />
              </button>
              <p className="mt-1.5 text-center text-[11.5px] leading-snug text-ink-muted">
                {t('plans.invoiceSub')}
              </p>
              <div className="my-3 border-t border-dashed border-edge" />
              <p className="mb-2 text-center text-[12px] font-semibold text-ink-soft">
                {t('plans.orPayByCard')}
              </p>
              {appPayButtons}
              <p className="mt-1.5 text-center text-[11.5px] leading-snug text-ink-muted">
                {t('plans.yearAccessNote')}
              </p>
            </>
          ) : (
            /* Recurring disabled (Payme outage) on a monthly plan: degrade to the
               one-time app-pay buttons rather than bricking checkout. The +10%
               amount is stated here too — the headline is the subscription
               price, and these buttons charge the one-time one. */
            <>
              {oneTimeLabel && (
                <p className="mb-2 text-center text-[13px] font-bold leading-snug text-ink">
                  {t('plans.onceAmount', { amount: oneTimeLabel })}
                </p>
              )}
              {appPayButtons}
              <p className="mt-2 text-center text-[11.5px] leading-snug text-ink-muted">
                {t('plans.payOnceLead')}
              </p>
            </>
          )}

          {/* Can't-pay escape hatch. The card-number step is the top drop-off
              point (the reason the reassurance strip exists), so a stuck buyer
              gets a direct line — and since 2026-09-20 that line is the
              operator's own Telegram (`COMPANY.telegramUrl`, @flesteyto), not
              the support form. A stuck payment is worth a person answering in
              minutes; the form is a ticket queue, and a buyer who has already
              failed to pay does not fill in a category and wait.
              ⚠️ Gated on HAS_TELEGRAM like every other render of that handle —
              a telegram-off deployment (KZ) keeps the form, which is the only
              channel it has. */}
          {HAS_TELEGRAM ? (
            <a
              href={COMPANY.telegramUrl}
              target="_blank"
              rel="noopener"
              className="mt-3 block w-full text-center text-[12px] font-medium text-ink-muted underline decoration-dotted underline-offset-2 transition-colors hover:text-brand"
            >
              {t('plans.cantPay')}
            </a>
          ) : (
            <ContactSupport
              defaultCategory="subscription"
              className="mt-3 block w-full text-center text-[12px] font-medium text-ink-muted underline decoration-dotted underline-offset-2 transition-colors hover:text-brand"
            >
              {t('plans.cantPay')}
            </ContactSupport>
          )}

          {/* While the sheet is open its own alert carries the error; the sheet
              resets the mutation on close, so this never shows a stale one. */}
          {checkout.isError && !onceOpen && (
            <p className="mt-2 text-[13px] text-status-closing-text" role="alert">
              {/* A recognised refusal gets a localized sentence; anything else
                  falls back to the generic one. The server's own message is
                  English and written for an API consumer, so it is never shown
                  to a buyer. */}
              {checkout.error instanceof ApiError && checkout.error.code === 'tin_required'
                ? t('plans.tinRequired')
                : t('plans.checkoutError')}
            </p>
          )}

          {/* Opened by the refusal above. Dismissing it leaves the plan card
              exactly as it was — the user can retry the purchase, or not. */}
          <CompanyInnModal
            open={tinNeeded}
            onClose={() => setTinNeeded(false)}
            onLater={() => setTinNeeded(false)}
          />

          {recurringEnabled && !isYearly && (
            <CardSaveModal
              open={subscribeOpen || (octoOpen && payMethod === 'payme')}
              mode="subscribe"
              planKey={subscribePlan.key}
              planName={planDisplayName(subscribePlan, t)}
              priceLabel={som(subscribePlan.priceUzs)}
              billingPeriod={subscribePlan.billingPeriod}
              metaEventId={subscribeMetaEventId}
              methodTabs={methodTabs}
              nicheHint={nicheHint}
              reminderChannel={reminderChannel}
              /* The member's IC fired on «Подписаться», i.e. on the sheet
                 OPENING — 388 of those against 54 typed card numbers over
                 16–22.09. This is the gesture that says the buyer is paying,
                 and it is the only Meta event fired from here: the IC is
                 deliberately left where it is. */
              onPaymentEntryStarted={() => fireAddPaymentInfo(subscribeMetaEventId, 'card')}
              // Only when Octobank is on offer: lets an unsupported (Visa/MC) card
              // recover in one tap instead of dead-ending on «Uzcard/Humo only».
              // Same switch the toggle uses — closes this modal, opens the OCTO flow.
              onSwitchToOcto={octoGateway ? () => switchMethod('octo') : undefined}
              // No entrance when the buyer merely swapped gateway — see `methodSwap`.
              enterAnimation={!methodSwap}
              onClose={closeSubscribeSheet}
              onSaved={() => {
                /*
                 * ⚠️ The card path used to CONFIRM NOTHING — it closed the modal
                 * and refreshed, leaving the pricing page looking untouched
                 * while money had moved. Both ways of paying now end on
                 * /billing/return.
                 *
                 * ⚠️ Still /billing/return, NOT straight to the tender the buyer
                 * came from. That return IS made — but from /billing/return, once
                 * its purchase-conversion query has settled. Jumping to the
                 * tender from here would skip the page that fires the BROWSER leg
                 * of the Meta purchase event, the one moment after payment when
                 * `_fbc`/`_fbp` exist; Subscribe would go back to being
                 * "Conversions API only" and attribute to an ad click zero times.
                 * One redirect, one place: billing-return.tsx.
                 *
                 * The refresh is AWAITED: `onSaved` runs while the modal's
                 * spinner is still up, so the wait is invisible and the return
                 * page arrives with the subscription already in session, able to
                 * name the plan instead of flashing generic copy. `.finally`,
                 * not `.then` — a failed refresh must still land the buyer on
                 * the confirmation rather than strand them here.
                 */
                void refresh().finally(() => {
                  setSubscribeOpen(false);
                  setOctoOpen(false);
                  void navigate('/billing/return?src=card');
                });
              }}
            />
          )}

          {/* OCTO-allowlisted users get the same subscribe CTA, but it opens the
              Octobank redirect modal (card entered on their hosted page) instead
              of the Payme inline form. Same plan + disclosure; the redirect + the
              webhook (activate + auto-renew on) do the rest. */}
          {octoGateway && !isYearly && (
            <OctoSubscribeModal
              open={octoOpen && payMethod === 'octo'}
              planKey={subscribePlan.key}
              planName={planDisplayName(subscribePlan, t)}
              priceLabel={som(subscribePlan.priceUzs)}
              billingPeriod={subscribePlan.billingPeriod}
              metaEventId={subscribeMetaEventId}
              methodTabs={methodTabs}
              nicheHint={nicheHint}
              reminderChannel={reminderChannel}
              /* «Продолжить на Octobank» — the member's qualified action. The id
                 is shared with the Payme body, so typing a digit and then
                 switching here still sends one AddPaymentInfo. */
              onProviderHandoff={() => fireAddPaymentInfo(subscribeMetaEventId, 'card')}
              onClose={() => setOctoOpen(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}

/** /plans — pricing page listing the active plans with a per-plan checkout CTA. */
export function Plans() {
  const t = useT();
  usePageTitle(t('plans.pageTitle'));
  const { locale } = useLocale();
  const { status, user } = useAuth();
  const isAuthed = status === 'authed';
  /*
   * The LIVE number for the social-proof line under the lead (owner,
   * 2026-10-13: the real count of registrations instead of a figure typed into
   * the copy). It rides on the public landing stats — Redis-cached for an hour
   * on the API, and the same query key as `/start` and `/ip`, so a visitor who
   * came through a landing already has it.
   *
   * For everyone, guests and signed-in alike: most buyers on this page are
   * registered free users. Never blocks anything — while it is pending, when it
   * fails and in the prerendered HTML (the prerender seeds only the price
   * list) the line below keeps its static text. No retry: one missed number is
   * not worth three more requests from a phone on a bad connection.
   */
  const { data: landingStats } = useQuery({
    queryKey: landingStatsQueryKey(locale),
    queryFn: () => fetchLandingStats(locale),
    staleTime: 10 * 60_000,
    retry: false,
  });
  const socialCount = socialProofCount(landingStats?.registeredUsersFloor);
  const [period, setPeriod] = useState<Period>('monthly');
  // Once we've landed a subscriber on their own period (or they've toggled by
  // hand), stop auto-switching so we don't fight the user.
  const periodPinned = useRef(false);
  // `source` is the arrival discriminator: /plans is reached from a paywall
  // CTA, a renewal reminder (?src=renewal), a landing CTA or direct, and until
  // now `plans_viewed` fired with no meta at all — so it was 100% null and you
  // could not ask what drove the view (events-health audit, 2026-08-19). Read
  // from the same ?src param the renewal step below already uses; default
  // 'direct' when absent.
  useEffect(() => {
    const source = new URLSearchParams(window.location.search).get('src') ?? 'direct';
    track('plans_viewed', { source });
    // Replay is a 10% sample, opted back to 100% only when a checkout OPENS —
    // so the buyer who reached /plans, read the prices and left without opening
    // one was recorded one time in ten. Measured 2026-09-28: 56 desktop sessions
    // viewed /plans, 2 had a recording. Owner's call the same day («хочу как
    // можно больше видеть»): record everyone from the moment they arrive here.
    // ~150–200 sessions/day on top of the sample, not the ~1 400 of the catalog.
    phStartRecording();
  }, []);
  /*
   * `?tender=<id>` — the lot the buyer came here to unlock, stashed so it can
   * survive the sign-in round trip AND the hosted provider's cross-site trip,
   * and be spent on /billing/return to put them back on it.
   *
   * Measured 2026-09-18 over 30 days: 75 of 98 new buyers had opened a specific
   * tender within the hour before paying, and only 45 returned to it after. The
   * id had nowhere to live between the paywall and the receipt.
   *
   * Written here rather than on the tender page so there is ONE writer: every
   * paywall CTA already routes through /plans, and a second stash point would
   * be a second thing to keep in sync.
   *
   * ⚠️ NO `intent` here, deliberately. Arriving on /plans is not a decision to
   * buy — it is one tap on a locked panel — and treating it as one is what sent
   * everyone who ever tapped a paywall back to /plans after signing in, with a
   * checkout sheet opening by itself. `postAuthPath` lands this population on
   * the tender instead.
   */
  const [pageParams] = useSearchParams();
  /*
   * `?checkout=failed|expired` — set by `GET /billing/checkout-link/:token` when
   * the tap on a prepared pay link could not create its order. Read after
   * mount, never during render: /plans is prerendered without a query, and a
   * banner in the first client render would not match the server HTML.
   */
  const [checkoutLinkFailed, setCheckoutLinkFailed] = useState(false);
  useEffect(() => {
    const v = new URLSearchParams(window.location.search).get('checkout');
    if (v === 'failed' || v === 'expired') setCheckoutLinkFailed(true);
  }, []);
  const tenderParam = pageParams.get('tender');
  useEffect(() => {
    if (tenderParam) stashPendingCheckout({ tenderId: tenderParam });
  }, [tenderParam]);
  /*
   * `?period=yearly` (also `annual`) opens the page on the «Год» tab — the link
   * under the auto-renewal notice («Год — 2 399 000 сум вместо 4 788 000», 09.10).
   * Anything else, or no parameter, keeps the default `monthly`. Pinned, so the
   * «land a subscriber on his own period» effect below does not flip a monthly
   * subscriber straight back to «Месяц». Applied in an effect, never during
   * render: /plans is prerendered without a query string.
   */
  const periodParam = pageParams.get('period');
  useEffect(() => {
    // A deployment that sells no year (Kazakhstan) ignores the link's tab.
    if (!countryConfig.payments.yearlyPlans) return;
    if (periodParam === 'yearly' || periodParam === 'annual') {
      setPeriod('annual');
      periodPinned.current = true;
    }
  }, [periodParam]);
  /*
   * Arrival from a renewal reminder. The reminder link carries ?src=renewal
   * (added so its revenue is attributable at all — Telegram sends no referrer,
   * so every recovered payment was landing in (direct)).
   *
   * Fired as its own event rather than a property on `plans_viewed` because the
   * renewal funnel needs it as a discrete step, and because it must survive the
   * user toggling period or reloading — hence the empty dep array, matching
   * plans_viewed.
   */
  useEffect(() => {
    const src = new URLSearchParams(window.location.search).get('src');
    if (src === 'renewal') track('renewal_reminder_opened');
  }, []);
  const {
    data: plans,
    isLoading,
    isFetching: plansFetching,
    refetch: refetchPlans,
  } = useQuery({
    queryKey: ['plans'],
    queryFn: fetchPlans,
    /*
     * The price list changes a few times a year; the app default (30 s) made
     * every return to the tab ask for it again. A failed re-ask flips the query
     * to `error` while the loaded list is still in `data`, and this page used to
     * branch on `isError` first — so tariffs that were on screen were replaced
     * by «Не удалось загрузить тарифы», together with every card and an open
     * payment sheet. Reproduced on prod 2026-10-02 as a guest: load → 32 s away
     * → back on the tab with the request failing → 0 prices, 0 buttons.
     * Ten minutes makes that re-ask rare; the branch below no longer depends on it.
     */
    staleTime: 10 * 60_000,
    // Nothing to show and the request failed: keep asking on our own, so the page
    // recovers the moment the connection does — not only on a tab switch.
    refetchInterval: (query) =>
      query.state.data === undefined && query.state.status === 'error' ? 5_000 : false,
  });
  /*
   * No tariffs to show and no first answer in flight: the request failed, or the
   * browser is offline and the query is parked (`fetchStatus: 'paused'` — neither
   * loading nor an error, which used to render an EMPTY grid). A list that is
   * already loaded stays on screen whatever a later refresh does.
   */
  const plansUnavailable = plans === undefined && !isLoading;
  // How often a visitor is left without tariffs was never measured — no event
  // existed. Once per mount; `offline` separates "no connection" from "API down".
  const loadFailureReported = useRef(false);
  useEffect(() => {
    if (!plansUnavailable || loadFailureReported.current) return;
    loadFailureReported.current = true;
    track('plans_load_failed', {
      offline: typeof navigator !== 'undefined' && navigator.onLine === false,
    });
  }, [plansUnavailable]);

  // Recurring rollout flag — governs whether test plans surface to this user
  // (allowlisted testers keep them). Authed-only; shares the PlanCard query.
  const { data: methodsData } = useQuery({
    queryKey: ['billing', 'methods'],
    queryFn: fetchPaymentMethods,
    enabled: isAuthed,
  });
  /*
   * Authed-only, and deliberately the ONLY clause. `testPlansEnabled` comes
   * from `PAYME_TEST_PLAN_ALLOWLIST_EMAILS`; the server's plan LISTING is gated
   * on `TEST_PLAN_ALLOWLIST_EMAILS`. A guest has neither, so a guest never sees
   * the 5 000 so'm card — which is the point (owner, 2026-09-22: a typed
   * address is not an identity, and the `test.uz` rule that made one count is
   * gone from both sides).
   */
  const testPlansEnabled = methodsData?.testPlansEnabled ?? false;

  // "Reached the tariff page" conversion — fired once on mount, to the browser
  // Pixel AND the server-side CAPI under one shared dedup id so ad-blocked
  // visitors still register (Meta counts the pair as a single event).
  useEffect(() => {
    const metaEventId = uuid();
    trackViewContent('plans', metaEventId);
    sendMetaEvent('ViewContent', metaEventId, 'plans');
  }, []);

  // Highlight the annual Pro as "best value" and show how much it saves vs
  // paying monthly for a year (Pro = no category limit; annual = not monthly).
  // Pin to keys, NOT to `categoryLimit == null` — the Бизнес plans share that
  // predicate, so a loose find() would compute the «×2» claim off the wrong
  // tier the moment prices move. This claim is printed on the pricing page.
  const plansList = plans ?? [];
  // A yearly plan's deal vs paying its OWN monthly tier for 12 months. Generic so
  // every yearly card (Pro, Бизнес, …) shows the same «Год за цену 6 месяцев —
  // экономия N» pitch, each computed off its own tier's prices.
  const buildDeal = (monthly?: Plan, annual?: Plan): YearlyDeal | undefined =>
    monthly && annual && monthly.priceUzs > 0
      ? {
          refYearUzs: monthly.priceUzs * 12,
          savingsUzs: monthly.priceUzs * 12 - annual.priceUzs,
          multiple: (monthly.priceUzs * 12) / annual.priceUzs,
          percent: Math.round((1 - annual.priceUzs / (monthly.priceUzs * 12)) * 100),
        }
      : undefined;
  /** The deal for a card, if it's a yearly plan — pairs it with its `-monthly` twin. */
  const dealFor = (plan: Plan): YearlyDeal | undefined =>
    plan.key.endsWith('-yearly')
      ? buildDeal(
          plansList.find((p) => p.key === plan.key.replace('-yearly', '-monthly')),
          plan,
        )
      : undefined;

  const monthlyPro = plansList.find((p) => p.key === 'pro-monthly');
  const annualPro = plansList.find((p) => p.key === 'pro-yearly');
  // Headline deal (drives the «Год» toggle's ×N / −% hint) — keyed to Pro.
  const deal = buildDeal(monthlyPro, annualPro);

  // Compact savings hint on the «Год» toggle: the ×N when the prices back a real
  // doubling, else the generic percent off.
  const x2 = dealMultiple(deal);
  const toggleHint = x2 ? t('plans.badgeX2', { multiple: x2 }) : deal ? `−${deal.percent}%` : null;

  // Land a paying subscriber on THEIR billing period, so their active plan renders
  // as «Текущий тариф» rather than a clickable «Подключить» (which risks re-paying).
  useEffect(() => {
    if (periodPinned.current || !plans || !user?.subscription || user.subscription.expired) return;
    const subPeriod = plans.find((p) => p.key === user.subscription?.plan)?.billingPeriod;
    if (subPeriod === 'monthly' || (subPeriod === 'annual' && countryConfig.payments.yearlyPlans)) {
      setPeriod(subPeriod);
      periodPinned.current = true;
    }
  }, [plans, user]);

  // The grid for the selected period: no Free card (funnel sells paid plans),
  // test plans only for allowlisted testers (their own dedicated allowlist).
  //
  // DETERMINISTIC order — Pro (the recommended tier) leads, then Бизнес, then ИП.
  // The cards used to render in the API's return order, which is createdAt-based
  // and so differs per DB (locally ИП was seeded before Pro, so ИП led; prod has
  // Pro first). Pinning the order here makes the grid stable everywhere instead
  // of tracking whatever order the plan rows happened to be inserted in.
  const tierRank = (key: string) =>
    key.startsWith('pro') ? 0 : key.startsWith('business') ? 1 : key.startsWith('ip') ? 2 : 3;
  /*
   * ИП is WITHDRAWN FROM SALE on UZ (2026-09-19). It was desktop-only from
   * 2026-09-06 ("it earns little and takes a third of the grid on a phone");
   * what closed it is the unit economics — LTV 148 000 so'm against a CAC of
   * 494 000 — so every ИП sale lost money and the card is gone from the grid
   * on every viewport, not merely hidden below `md`.
   *
   * REMOVED FROM THE DOM, not CSS-hidden. The previous `hidden md:block` kept
   * the price in the prerendered HTML deliberately (crawlers + the smoke
   * price-floor saw it); a price in crawlable HTML is an offer, and we no
   * longer make this one. The floor in `.gitlab-ci.yml` («/plans crawlable
   * prices») is 2, and the two monthly tiers still on sale — Pro and Бизнес —
   * satisfy it. The schema.org offers below are filtered by the same rule, so
   * the structured data cannot advertise a plan the grid does not sell.
   *
   * UZ ONLY, keyed on `countryConfig.code`. KZ has no «Бизнес» tier
   * (`features.teamPlans: false` — the server drops the `business-*` keys), so
   * its monthly grid is ИП + Pro; withdrawing ИП there would leave a one-card
   * pricing page. KZ pricing is a separate decision nobody has made.
   *
   * A user whose CURRENT (unexpired) plan is ИП still sees their own card, as
   * «Текущий тариф» — they keep paying and renewing, and the page must not
   * pretend the plan they are on never existed.
   */
  const ipWithdrawn = countryConfig.code === 'uz';
  const isIpKey = (key: string) => key.startsWith('ip');
  const activePlanKey =
    user?.subscription && !user.subscription.expired ? user.subscription.plan : null;
  const onIp = typeof activePlanKey === 'string' && isIpKey(activePlanKey);
  const hideIp = ipWithdrawn && !onIp;
  const visible = plansList
    .filter((p) => {
      if (p.priceUzs === 0) return false;
      if (p.key.startsWith('test') && !testPlansEnabled) return false;
      if (hideIp && isIpKey(p.key)) return false;
      return p.billingPeriod === period;
    })
    .sort((a, b) => tierRank(a.key) - tierRank(b.key));
  /*
   * Three cards must fit INLINE on an ordinary laptop. `minmax(280px,360px)`
   * only packs a third column once all three fit at their MAX width (1112px of
   * container, ≈1440px of viewport with the sidebar), so on a 1366px screen ИП
   * wrapped alone onto a second row. With three or more cards the tracks are
   * `minmax(280px,1fr)` capped by `max-w-[1112px]`: three columns from 872px of
   * container, growing to 360px each. Two cards keep the fixed 360px tracks so
   * they do not balloon on a wide screen.
   *
   * Since ИП was withdrawn (above), THREE cards is the ИП-subscriber case only:
   * UZ now renders two on both the monthly and the yearly grid, so both are
   * centred instead of left-aligned across three columns.
   */
  const wideGrid = visible.length >= 3;
  // Fewer than three cards (UZ monthly and yearly = Pro + Бизнес; KZ monthly =
  // ИП + Pro; KZ yearly = 1) keep the fixed 360px tracks so they don't balloon on
  // a wide screen — AND cap the grid to the width of two cards so `mx-auto`
  // centres them. Without the cap the full-width grid left-aligns the cards with
  // dead space on the right (the gap a missing third tier leaves).
  const gridClass = wideGrid
    ? 'max-w-[1112px] grid-cols-[repeat(auto-fit,minmax(280px,1fr))]'
    : 'max-w-[760px] grid-cols-[repeat(auto-fit,minmax(280px,360px))]';

  return (
    <>
      <Seo
        // `plans.pageTitle` alone is «Тарифы» — a 21-character title on the one page
        // whose entire job is answering "what does it cost", and `plans.title` is the
        // paywall modal's headline reused as a 45-character description. Both are
        // well under what a SERP renders, and neither carries a price or a currency.
        title={t('plans.seoTitle')}
        description={t('plans.seoDesc')}
        canonical="/plans"
        lang={locale}
        // /plans was the ONLY page type on the site with no structured data at all
        // (checked across 34 live pages, 2026-08-12).
        //
        // Built from the SAME `plans` the cards render, so the schema cannot drift
        // from the visible price — and emitted only when that data is present, so a
        // prerender whose plans fetch failed ships no schema rather than a schema
        // claiming a price of 0. Prices are `priceUzs`, the subscription figure, not
        // `priceUzsOneTime` (the +10% single-payment variant): the subscription price
        // is the headline the card leads with.
        //
        // ⚠️ This lists ALL billing periods, not just the one the Месяц/Год toggle has
        // selected. The toggle is client state; the schema describes the product.
        //
        // ИП is excluded wherever it is withdrawn (`ipWithdrawn`, see below) — an
        // Offer is a machine-readable claim that the price is buyable, and it is
        // not. Keyed on the COUNTRY, not on `hideIp`: the viewer's own plan must
        // not change what the structured data says the product costs.
        jsonLd={
          plans && plans.length > 0
            ? {
                '@context': 'https://schema.org',
                '@graph': [
                  {
                    '@type': 'Service',
                    name: countryConfig.brand.name,
                    serviceType: t('plans.seoTitle'),
                    description: t('plans.seoDesc'),
                    url: `${SITE_ORIGIN}${localePath('/plans', locale)}`,
                    provider: {
                      '@type': 'Organization',
                      name: countryConfig.brand.name,
                      url: `${SITE_ORIGIN}/`,
                    },
                    areaServed: { '@type': 'Country', name: countryNameEn(countryConfig.code) },
                    offers: plans
                      .filter(
                        (p) =>
                          !p.key.startsWith('test') &&
                          p.priceUzs > 0 &&
                          !(ipWithdrawn && isIpKey(p.key)),
                      )
                      .map((p) => {
                        // Two plans share a display name across billing periods
                        // (monthly + annual are both "Pro"), which produced two Offers
                        // both literally named "Pro" with no property telling them
                        // apart. Suffix the period, and attach a UnitPriceSpecification
                        // whose billingDuration (P1M / P1Y) makes the recurrence
                        // machine-readable.
                        const isMonthly = p.billingPeriod === 'monthly';
                        const periodLabel = isMonthly
                          ? t('plans.toggleMonthly')
                          : t('plans.toggleYearly');
                        return {
                          '@type': 'Offer',
                          name: `${p.name} — ${periodLabel}`,
                          price: p.priceUzs,
                          priceCurrency: countryConfig.currency.code,
                          url: `${SITE_ORIGIN}${localePath('/plans', locale)}`,
                          availability: 'https://schema.org/InStock',
                          priceSpecification: {
                            '@type': 'UnitPriceSpecification',
                            price: p.priceUzs,
                            priceCurrency: countryConfig.currency.code,
                            billingDuration: isMonthly ? 'P1M' : 'P1Y',
                          },
                        };
                      }),
                  },
                  {
                    // Home → Plans breadcrumb (rendered in the SERP).
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                      {
                        '@type': 'ListItem',
                        position: 1,
                        name: t('nav.catalog'),
                        item: `${SITE_ORIGIN}${localePath('/', locale)}`,
                      },
                      {
                        '@type': 'ListItem',
                        position: 2,
                        name: t('plans.pageTitle'),
                        item: `${SITE_ORIGIN}${localePath('/plans', locale)}`,
                      },
                    ],
                  },
                ],
              }
            : undefined
        }
      />

      <div className="mx-auto w-full max-w-6xl">
        {/*
         * ⚠️ The heading is VISIBLE, and the lead is the page's own copy.
         *
         * This used to be an `sr-only` h1 plus a borrowed line: `plans.title` is
         * the PAYWALL MODAL's headline (it sits with `plans.eyebrow`,
         * `plans.cta` and `plans.dismiss`), reused here as a lead. The result
         * was a page with no visible title at all and one small grey sentence
         * left-aligned above a centred layout — reading as a stray fragment
         * rather than an introduction.
         *
         * Borrowed copy also drifts: an edit made for the modal, where the
         * sentence interrupts someone mid-task, silently rewrites the pricing
         * page, where it is the first thing a buyer reads.
         *
         * The lead states the actual free/paid split rather than selling: the
         * full description, specification, requirements, procedure and AI
         * summary are FREE (commit b4e17f5 deliberately reversed that scope), so
         * claiming the tariff unlocks understanding would be a false claim about
         * what it adds. It unlocks ACTING on a tender.
         */}
        <div className="mb-7 text-center">
          <h1 className="font-brand text-[26px] font-extrabold tracking-[-0.4px] text-ink sm:text-[32px]">
            {t('plans.pageTitle')}
          </h1>
          <p className="mx-auto mt-2 max-w-[560px] text-[14.5px] leading-[1.55] text-ink-soft">
            {t('plans.pageLead')}
          </p>
          {/* Social proof. `plans.socialLive` carries the LIVE count of
              registrations (rounded down to a hundred) once it has arrived and
              is big enough to be worth saying — see `socialProofCount`: never
              «более 0», and nothing below 500. Until then, and whenever the
              number is unavailable, `plans.social` — the static line of this
              country build (a number-free one on Kazakhstan). One <p> either
              way, so the swap moves nothing on the page. */}
          <p data-testid="plans-social" className="mt-3 text-[13.5px] font-semibold text-brand">
            {socialCount !== null
              ? t('plans.socialLive', { count: socialCount })
              : t('plans.social')}
          </p>
        </div>

        {/* The followed pay link could not create its order (`?checkout=failed`)
            or had expired (`?checkout=expired`) — the API sends the buyer back
            here rather than showing a raw error from its own domain. */}
        {checkoutLinkFailed && (
          <p
            data-testid="checkout-link-failed"
            role="alert"
            className="mx-auto mb-5 max-w-[560px] rounded-md border border-status-closing-text/30 bg-bone-surface px-4 py-3 text-center text-[13.5px] leading-snug text-status-closing-text"
          >
            {t('plans.checkoutLinkFailed')}
          </p>
        )}

        {isLoading ? (
          <div className="mx-auto grid w-full grid-cols-[repeat(auto-fit,minmax(280px,360px))] items-stretch justify-center gap-4">
            {Array.from({ length: 2 }).map((_, i) => (
              <PlanCardSkeleton key={i} />
            ))}
          </div>
        ) : plansUnavailable ? (
          <div
            data-testid="plans-load-error"
            role="alert"
            className="rounded-lg border border-edge bg-bone-surface p-8 text-center text-[13px] text-status-closing-text shadow-m-sm"
          >
            <p>{t('plans.loadError')}</p>
            {/* The page also retries by itself every 5 s; the button is for the
                person who has just got their connection back and will not wait. */}
            <button
              type="button"
              onClick={() => void refetchPlans()}
              disabled={plansFetching}
              className="mt-4 inline-flex h-10 items-center justify-center rounded-md border border-edge bg-white px-5 text-[13.5px] font-semibold text-ink shadow-m-sm disabled:opacity-60"
            >
              {t('plans.loadRetry')}
            </button>
          </div>
        ) : (
          <>
            {/* No «Месяц / Год» switch where no year is sold (Kazakhstan). */}
            {countryConfig.payments.yearlyPlans && (
              <div className="text-center">
                <PeriodToggle
                  period={period}
                  onChange={(p) => {
                    /*
                     * The yearly plans, and everything that only exists on them,
                     * live behind this control — including «оплата по счёту»,
                     * which is rendered under `isYearly` alone.
                     *
                     * Without this event that whole branch has no denominator.
                     * Measured 2026-08-14: `invoice_requested` had ZERO rows since
                     * it shipped, against 576 `plans_viewed`, and it was
                     * impossible to say whether nobody wanted an invoice or
                     * nobody ever reached the tab it sits on. That mattered more
                     * than it sounds: no plan of 500 000 so'm or more has ever
                     * been paid by card (21 attempts, 0 completions), so bank
                     * transfer is currently the ONLY working route to a yearly
                     * subscription and not one step of it was measurable.
                     *
                     * Emitted from the USER's toggle only. The effect above sets
                     * the same state programmatically, to land a subscriber on
                     * their own billing period — counting that would report
                     * interest in yearly that nobody expressed.
                     */
                    track('plan_period_toggled', { to: p, pinned: periodPinned.current });
                    periodPinned.current = true;
                    setPeriod(p);
                  }}
                  hint={toggleHint}
                />
              </div>
            )}

            <div
              className={`plans-cards mx-auto grid w-full items-stretch justify-center gap-4 ${gridClass}`}
            >
              {visible.map((plan) => {
                const isRecommended = plan.key === 'pro-monthly' || plan.key === 'pro-yearly';
                // Pro is centered ONLY when all three cards fit inline (container
                // ≥872px — see the @container rule in globals.css), which since the
                // withdrawal is the ИП-subscriber grid alone: ИП (cheapest) first,
                // then Pro, then Бизнес. Everywhere else the cards keep DOM order,
                // and with ИП gone that is Pro, then Бизнес.
                const isIp = isIpKey(plan.key);
                const slotClass = isRecommended
                  ? 'plans-slot-pro'
                  : isIp
                    ? 'plans-slot-cheap'
                    : 'plans-slot-premium';
                return (
                  <div
                    key={plan.id}
                    className={slotClass}
                    data-testid={isIp ? 'plan-slot-ip' : undefined}
                  >
                    <PlanCard plan={plan} recommended={isRecommended} deal={dealFor(plan)} />
                  </div>
                );
              })}
              {/* The trust strip and the consent line live INSIDE the cards grid
                  as full-row children: `col-span-full` gives them exactly the
                  width of the card tracks (collapsed auto-fit tracks are 0 wide),
                  so their edges line up with the outer cards on every viewport.
                  A separate `max-w-3xl` wrapper sat 16px proud of the two-card
                  grid on each side and read as misaligned (2026-09-06).
                  `order-last` keeps them below the cards under the container
                  query that reorders the card slots. */}
              <div className="order-last col-span-full mt-2">
                <CheckoutTrust wide={wideGrid} />
              </div>
              {/*
               * Card-on-file / auto-renew management — self-hides for anyone
               * without a live paid subscription.
               *
               * It sat directly under the page lead, ABOVE the period toggle
               * and the cards, until 2026-09-19. Moving the auto-renew switch
               * to the bottom of the panel (same day) changed nothing for a
               * real subscriber, because the PANEL itself was the first thing
               * on the page: «Удалить карту» and the toggle were still the
               * first controls he met, just a few lines lower inside the same
               * block. Igor, reading prod: «Удалить карту и отключить
               * автопродление у меня нет внизу — оно так же вверху».
               *
               * So the block moves as a whole: a subscriber now passes the
               * plans, the price he is paying and the four trust cards before
               * he reaches the two controls that end his subscription. It
               * stays INSIDE the cards grid as a `col-span-full` child (same
               * reason as the trust strip and the consent line — collapsed
               * auto-fit tracks are 0 wide, so a `col-span-full` child is
               * exactly as wide as the card tracks and its edges line up with
               * the outer cards), and `order-last` keeps all three below the
               * cards under the container query that reorders the slots.
               *
               * Nothing inside the panel changed: same «Текущий тариф» badge
               * on the subscriber's own card above, same `?attach=card` deep
               * link from the renewal reminder and /billing/return, same
               * events. The deep link opens the card modal on arrival, and
               * since the panel is no longer on-screen when it does, the panel
               * scrolls itself into view — see `subscription-management.tsx`.
               */}
              <div className="order-last col-span-full">
                <SubscriptionManagement />
              </div>
              <div className="order-last col-span-full">
                <ConsentLine />
              </div>
            </div>
          </>
        )}

        {/* Payment history — reference, below the grid (self-hides for guests). */}
        <PaymentHistory />
      </div>
    </>
  );
}
